<template>
  <Dashboard title="2d Demo Viewer" iconsOnly="true" noPadding="true">
    <div class="w-full h-full flex flex-col text-center items-center justify-center gap-4" v-if="demoStatus !== 'ready' && demoStatus !== 'failed'">
      <!-- loading spin wheel of height and width of 16 -->
      <svg
        class="animate-spin h-16 w-16 text-primary-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2
          5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
          5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <h1 class="text-text-primary text-bold text-xl">Demo processing...</h1>
      <h2 class="text-text-secondary w-72">
        Your demo is currently being processed, this could take up to 5 minutes. Go shoot some heads, we will update this page when it's ready.
      </h2>
    </div>
    <div class="w-full h-full flex flex-col text-center items-center justify-center gap-4" v-else-if="demoStatus === 'failed'">
      <div class="w-16 h-16 rounded-full bg-danger-900 flex justify-center items-center">
        <img class="h-10" src="../assets/icons/error.svg">
      </div>
      <h1 class="text-text-primary text-bold text-xl">Demo loading failed</h1>
      <h2 class="text-text-secondary w-72">
        Your demo has failed to process, create a support ticket on our <a href="https://discord.gg/Refrag" target="_blank" class="cursor-pointer underline text-text-primary" >discord</a> so we get this issue resolved. Make sure to include this demo ID in your ticket: <span class="text-danger-500">{{ route.params.id }}</span>
      </h2>
      <router-link to="/demos" class="px-4 py-3 bg-gray-800 rounded-md flex gap-2 items-center text-text-primary">
        <img class="h-4 w-4 brightness-200" src="../assets/icons/leftarrow.svg" />
        Go back
      </router-link>
    </div>
    <div v-else class="h-full w-full flex flex-col">
      <div class="w-full flex-1 flex">
        <div class="w-full flex-1">
          <div
            class="h-full w-full relative flex"
            :class="[isDrawing ? 'cursor-pencil' : 'cursor-grab']"
            id="stageParent"
            ref="stageParent"
          >
            <div class="absolute z-50 top-0 left-0 p-6 flex gap-3">
              <router-link
                class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300"
                to="/demos"
              >
                <img class="h-4 w-4 brightness-200" src="../assets/icons/leftarrow.svg" />
              </router-link>
              <Popover as="div" class="relative inline-block text-left">
                <div>
                  <PopoverButton
                    class="group flex justify-center items-center rounded-md bg-gray-800 duration-200 w-8 h-8 text-left text-sm font-medium text-text-primary hover:bg-gray-700 focus:outline-none focus:ring-0"
                  >
                    <img class="h-4" src="../assets/icons/settings.svg">
                  </PopoverButton>
                </div>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <PopoverPanel
                    class="absolute w-[240px] p-4 mt-2 origin-top rounded-md border border-gray-700 shadow-lg outline-none bg-gray-950"
                  >
                    <div class="flex flex-col justify-center mb-4">
                      <div class="flex gap-x-2 items-center pb-2 text-text-secondary uppercase text-xs tracking-[1.5px]">
                        Timeline Filters
                      </div>
                      <div class="flex py-2 justify-center items-center">
                        <h2 class="text-sm text-text-primary flex-1">Show Round Info</h2>
                        <Switch
                          v-model="showRoundInfo"
                          :class="showRoundInfo ? 'bg-primary-500' : 'bg-gray-600'"
                          class="relative inline-flex h-3 w-5 items-center rounded-full"
                        >
                          <span
                            :class="showRoundInfo ? 'translate-x-[9px] bg-primary-50' : 'translate-x-[1px] bg-gray-800'"
                            class="inline-block h-2.5 w-2.5 transform rounded-full transition"
                          />
                        </Switch>
                      </div>

                      <div class="flex justify-center items-center">
                        <h2 class="text-sm text-text-primary flex-1">Show Grenade Info</h2>
                        <Switch
                          v-model="showGrenadeGroups"
                          :class="showGrenadeGroups ? 'bg-primary-500' : 'bg-gray-600'"
                          class="relative inline-flex h-3 w-5 items-center rounded-full"
                        >
                          <span
                            :class="showGrenadeGroups ? 'translate-x-[9px] bg-primary-50' : 'translate-x-[1px] bg-gray-800'"
                            class="inline-block h-2.5 w-2.5 transform rounded-full transition"
                          />
                        </Switch>
                      </div>
                    </div>
                    <div class="flex flex-col gap-2">
                      <div class="flex gap-x-2 items-center pb-2 text-text-secondary uppercase text-xs tracking-[1.5px]">
                        Key binds
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Play/Pause</h1>
                        <h2 class="text-text-secondary text-sm">Space</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Next Round</h1>
                        <h2 class="text-text-secondary text-sm">N</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Previous Round</h1>
                        <h2 class="text-text-secondary text-sm">P</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Forward 2 seconds</h1>
                        <h2 class="text-text-secondary text-sm"><img src="../assets/icons/rightarrow.svg"></h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Back 2 seconds</h1>
                        <h2 class="text-text-secondary text-sm"><img src="../assets/icons/leftarrow.svg"></h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Fast Forward</h1>
                        <h2 class="text-text-secondary text-sm">F</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Slow Down</h1>
                        <h2 class="text-text-secondary text-sm">S</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Toggle Draw/Drag</h1>
                        <h2 class="text-text-secondary text-sm">D</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Toggle Upper/Lower Map</h1>
                        <h2 class="text-text-secondary text-sm">M</h2>
                      </div>
                      <div class="flex font-light">
                        <h1 class="text-text-primary text-sm flex-1">Clear Drawing</h1>
                        <h2 class="text-text-secondary text-sm">C</h2>
                      </div>
                    </div>

                  </PopoverPanel>
                </transition>
              </Popover>
              <div @click="setIsDrawing(true)" class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300"
                :class="[isDrawing ? 'border-primary-600 border-2' : 'border-none']">
                <PencilIcon class="h-4 w-4 text-text-secondary"/>
              </div>
              <div @click="setIsDrawing(false)" class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300"
                :class="[!isDrawing ? 'border-primary-600 border-2' : 'border-none']">
                <HandRaisedIcon class="h-4 w-4 text-text-secondary"/>
              </div>
              <div @click="clearLines()" class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300">
                <TrashIcon class="h-4 w-4 text-text-secondary"/>
              </div>
            </div>
            <div v-if="roundData" class="flex flex-col gap-2 items-center absolute z-50 top-6 left-1/2 -translate-x-1/2">
              <h2 class="text-text-secondary center text-xs tracking-[1.5px] uppdercase">Round {{ currentRound }}</h2>
              <div v-if="bombPlantEvent && bombPlantEvent.tick < currentTick && !roundEnded" class="flex gap-1 items-center">
                <img class="h-5 filter-red" src="../assets/icons/c4.svg">
                <h1 class="center text-semibold tracking-[0.5px] leading-[20px] text-danger-500"
                >{{ moment.unix(getTimeOfTick(currentTick)).format('ss.S') }}</h1>
              </div>
              <div v-else class="flex items-center">
                <h1 class="center text-semibold tracking-[0.5px] leading-[20px] text-text-primary"
                >{{ moment.unix(getTimeOfTick(currentTick)).format('m:ss') }}</h1>
              </div>

            </div>
            <div v-if="roundData" class="flex flex-col gap-2 absolute z-50 top-6 right-6 text-white">
              <div v-for="kill in activeKills" class="flex rounded-lg items-center justify-end">
                <div class="bg-gray-900 opactiy-80 flex py-2 px-3 gap-3 items-center">
                  <img v-if="kill.attackerBlinded" class="h-4" src="../assets/icons/flashed_kill.svg">
                  <h2 class="text-sm"
                    :class="[kill.attackerSide === 'CT' ? 'text-text-ct' : 'text-text-t']"
                  >
                    {{ kill.attackerName }}
                  </h2>
                  <img class="h-3" :src="'/img/icons/' + kill.weapon + '.png'">
                  <img v-if="kill.noScope" class="h-4" src="../assets/icons/noscope.svg">
                  <img v-if="kill.thruSmoke" class="h-4" src="../assets/icons/smoke_kill.svg">
                  <img v-if="kill.isWallbang" class="h-4" src="../assets/icons/wallbang.svg">
                  <img v-if="kill.isHeadshot" class="h-4" src="../assets/icons/headshot.svg">
                  <h2 class="text-sm"
                      :class="[kill.victimSide === 'CT' ? 'text-text-ct' : 'text-text-t']"
                  >
                    {{ kill.victimName }}
                  </h2>
                </div>

              </div>
            </div>
            <v-stage ref="stage" :config="stageConfig" @mousedown="startDrawing" @mouseup="endDrawing" @mousemove="draw" :draggable="!isDrawing">
              <v-layer ref="layer">
                <v-image @click="toggleMaps()" ref="map" :config="mapImageConfig"></v-image>
                <v-line v-for="(line, index) in lines" :key="index" :config="line" />
                <v-group ref="group"></v-group>
              </v-layer>
            </v-stage>
          </div>
        </div>

        <div class="w-96 bg-gray-900 flex-none relative overflow-y-auto">
          <div class="w-full absolute top-0">
            <div
              v-if="roundData"
              class="flex items-stretch justify-center flex-none"
            >
              <div
                class="w-96 h-full bg-gray-950 flex flex-col items-stretch border-l border-gray-800"
              >
                <div class="justify-start p-4 bg-gray-950 flex items-center gap-3 border-b border-gray-800">
                  <img class="w-8 h-8" src="../assets/icons/ctbadge.png">
                  <h1 class="text-white w-full text-lg font-bold flex-1">
                    {{ ctPlayers[0]?.team || "CT" }}
                  </h1>
                  <h2 class="text-text-ct text-lg font-bold">
                    {{ roundData["cTScore"] }}
                  </h2>
                </div>

                <div class="w-full h-full flex items-center flex-col even:bg-gray-900 relative" v-for="player in sortedCTPlayers">
                  <div class="flex w-full">
                    <div class="flex flex-col w-full px-4 py-3 gap-1.5"
                      :class="[player.hp === 0 ? 'bg-gray-900' : '']">
                      <div class="flex items-center gap-1">
                        <p v-if="player.armor" class="text-text-primary text-sm">{{ player.armor }}</p>
                        <span v-if="player.armor" class="text-gray-600 text-sm">•</span>
                        <p class="text-sm flex-1"
                          :class="[player.hp === 0 ? 'text-text-disabled' : 'text-text-primary']">
                        {{ player.playerName }}</p>

                        <img
                          v-if="getPrimary(player)"
                          class="h-4"
                          :src="'/img/icons/' + getPrimary(player).weaponName + '.png'"
                        />
                      </div>
                      <div class="flex items-center gap-1">
                        <img
                          v-if="player.hasHelmet"
                          class="h-4 brightness-75"
                          src="../assets/icons/headarmor.svg"
                        />
                        <img
                          v-else-if="player.armor > 0"
                          class="h-4 brightness-75"
                          src="../assets/icons/armor.svg"
                        />
                        <img
                          v-if="player.hasDefuse"
                          class="h-3 brightness-75"
                          src="../assets/icons/defuser.svg"
                        />
                        <p class="text-sm flex-1"
                          :class="[player.hp === 0 ? 'text-text-disabled' : 'text-text-secondary']">
                        ${{ player.cash }}</p>
                        <div v-for="item in getSortedInventory(player.inventory)">
                          <img
                            v-if="
                              item.weaponClass === 'Grenade' &&
                              getPrimary(player).weaponName !== item.weaponName
                            "
                            class="h-4 brightness-75"
                            :src="'/img/icons/' + item.weaponName + '.png'"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-if="player.hp === 0" class="absolute right-3 h-full flex items-center justify-center">
                      <img class="h-4" src="../assets/icons/skull.svg" />
                    </div>
                  </div>

                  <div class="w-full bg-[#23354e] absolute bottom-0 h-0.5">
                    <div
                      class="h-full bg-text-ct"
                      :style="{
                        width: `${player.hp}%`,
                      }"
                    ></div>
                  </div>
                </div>

                <div class="justify-start p-4 bg-gray-950 flex items-center gap-3 border-b border-gray-800">
                  <img class="w-8 h-8" src="../assets/icons/tbadge.png">
                  <h1 class="text-white w-full text-lg font-bold flex-1">
                    {{ tPlayers[0]?.team || "T" }}
                  </h1>
                  <h2 class="text-text-t text-lg font-bold">
                    {{ roundData["tScore"] }}
                  </h2>
                </div>

                <div class="w-full h-full flex items-center flex-col even:bg-gray-900 relative" v-for="player in sortedTPlayers">
                  <div class="flex w-full relative">
                    <div class="flex flex-col w-full px-4 py-3 gap-1.5"
                      :class="[player.hp === 0 ? 'bg-gray-900' : '']">
                      <div class="flex items-center gap-1">
                        <p v-if="player.armor" class="text-text-primary text-sm">{{ player.armor }}</p>
                        <span v-if="player.armor" class="text-gray-600 text-sm">•</span>
                        <p class="text-sm flex-1"
                          :class="[player.hp === 0 ? 'text-text-disabled' : 'text-text-primary']">
                        {{ player.playerName }}</p>

                        <img
                          v-if="getPrimary(player)"
                          class="h-4"
                          :src="'/img/icons/' + getPrimary(player).weaponName + '.png'"
                        />
                      </div>
                      <div class="flex items-center gap-1">
                        <img
                          v-if="player.hasHelmet"
                          class="h-4 brightness-75"
                          src="../assets/icons/headarmor.svg"
                        />
                        <img
                          v-else-if="player.armor > 0"
                          class="h-4 brightness-75"
                          src="../assets/icons/armor.svg"
                        />
                        <img
                          v-if="player.hasDefuse"
                          class="h-3 brightness-75"
                          src="../assets/icons/defuser.svg"
                        />
                        <p class="text-sm flex-1"
                          :class="[player.hp === 0 ? 'text-text-disabled' : 'text-text-secondary']">
                        ${{ player.cash }}</p>
                        <div v-for="item in getSortedInventory(player.inventory)">
                          <img
                            v-if="
                              item.weaponClass === 'Grenade' &&
                              getPrimary(player).weaponName !== item.weaponName
                            "
                            class="h-4 brightness-75"
                            :src="'/img/icons/' + item.weaponName + '.png'"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-if="player.hp === 0" class="absolute right-3 h-full flex items-center justify-center">
                      <img class="h-4" src="../assets/icons/skull.svg" />
                    </div>
                  </div>

                  <div class="w-full bg-[#23354e] h-0.5 absolute bottom-0">
                    <div
                      class="h-full bg-text-t"
                      :style="{
                        width: `${player.hp}%`,
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            <!-- loading spinner -->
            <div
              v-else
              class="w-full h-full flex mt-10 items-center justify-center"
            >
              <svg
                class="animate-spin h-16 w-16 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0
                  12h4zm2 5.291A7.962 7.962 0 014 12H0c0
                  3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- round bar -->
      <div class="w-full h-24 flex border-t border-gray-700">
        <div v-if="roundData" class="w-full flex flex-col items-center">
          <div class="w-full flex bg-gray-900 flex-1">
            <button
              class="w-20 flex items-center justify-center focus:outline-none border-r border-gray-700 bg-primary-600"
              @keydown.space="(event) => event.preventDefault()"
              @click="togglePlay()"
            >
              <img v-if="!playDemo" class="h-4" src="/img/icons/play.svg" />
              <img v-else class="h-4" src="/img/icons/pause.svg" />
            </button>
            <button
              class="w-20 flex items-center justify-center focus:outline-none border-r border-gray-700 text-white"
              @click="updateFrameRate()"
            >
              {{ frameRate / 64 }}x
            </button>
            <div class="w-full bg-gray-900 relative">
              <input
                id="default-range"
                type="range"
                step="1"
                min="0"
                :onMousedown="() => sliderActive = true"
                :onMouseup="() => sliderActive = false"
                v-model="sliderFrame"
                :max="roundData.frames.length"
                class="w-full h-full slider z-30 absolute"
              />
              <div
                class="h-full bg-gray-500 bg-opacity-30 absolute z-20 border-r-2 border-gray-300"
                :style="{
                  width: `${(sliderFrame / roundData.frames.length) * 100}%`,
                }"
              />
              <div class="w-full h-full flex divide-x divide-gray-700 divide-dashed">
                <div v-for="index in 8" :key="index" class="h-full w-full"></div>
              </div>
              <div class="w-full h-full flex absolute top-0">
                <div
                  v-if="showGrenadeGroups"
                  v-for="grenadeGroup in getTimelineEvents().grenadeGroups"
                  class="h-full border-gray-500 absolute flex justify-end select-none"
                  :style="[{ width: `${getTickTime(grenadeGroup[0])}%` }]"
                >
                  <div class="z-50 group flex h-full items-center justify border-r-2 border-gray-500 border-dashed">
                    <div @click="selectGrenadeGroup(grenadeGroup)" class="h-6 w-6 bg-gray-500 group-hover:scale-105 cursor-pointer duration-300 translate-x-[13px] rounded-full text-white text-xs flex items-center justify-center outline outline-gray-900">
                      {{ grenadeGroup.length }}
                    </div>
                  </div>

                  <div class="absolute bg-gray-800 -translate-y-full -mt-2 py-2 translate-x-1/2 rounded-md border-gray-700 border grid gap-3" v-if="selectedGrenadeGroupId === grenadeGroup[0].uniqueID">
                    <div class="w-full flex gap-2 px-3 items-center" v-for="grenade in grenadeGroup">
                      <h2 class="text-text-secondary text-sm">{{ moment.unix(getTimeOfTick(grenade.throwTick)).format('m:ss') }}</h2>
                      <h1 class="text-sm flex-1"
                        :class="[grenade.throwerSide === 'CT' ? 'text-text-ct' : 'text-text-t']"
                      >
                        {{ getGrenadeThrowerName(grenade) }}
                      </h1>
                      <img class="max-w-[16px]" :src="'/img/icons/' + grenade.grenadeType + '.png'">
                      <h2 v-if="grenade.grenadeSimMode < 0" class="text-primary-400 text-sm underline cursor-pointer hover:text-primary-300 duration-300"
                          v-clipboard:copy="
                            `setpos ${grenade.throwerX} ${grenade.throwerY} ${grenade.throwerZ}; setang ${grenade.throwerViewY} ${grenade.throwerViewX}`
                          "
                          v-clipboard:success="copySucceeded"
                      >Copy</h2>
                      <h2 v-else class="text-primary-400 text-sm underline cursor-pointer hover:text-primary-300 duration-300"
                          @click="launchGrenade(grenade)"
                      >Launch</h2>
                    </div>
                  </div>
                </div>
                <div
                  v-if="showRoundInfo"
                  v-for="kill in getTimelineEvents().kills"
                  class="h-full border-red-500 absolute flex justify-end"
                  :style="[{ width: `${getTickTime(kill)}%` }]"
                >
                  <div
                    class="w-[1px] h-full absolute z-50 cursor-pointer"
                    :style="[
                      kill.attackerSide === 'CT'
                        ? { 'background-color': '#687cca' }
                        : { 'background-color': '#e38617' },
                    ]"
                  ></div>
                </div>
                <div v-if="getTimelineEvents().plantEvent && showRoundInfo">
                  <div
                    class="h-full absolute flex justify-end"
                    :style="[{ width: `${getTickTime(getTimelineEvents().plantEvent)}%` }]"
                  >
                    <div class="z-25 flex h-full items-center justify border-r border-danger-500 border-dashed">
                      <div class="h-6 w-6 bg-danger-500 translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900">
                        C4
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="getTimelineEvents().defuseEvent && showRoundInfo">
                  <div
                    class="h-full border-red-500 absolute flex justify-end"
                    :style="[{ width: `${getTickTime(getTimelineEvents().defuseEvent)}%` }]"
                  >
                    <div class="z-25 flex h-full items-center justify border-r border-text-ct border-dashed">
                      <div class="h-6 w-6 bg-text-ct translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900">
                        <img class="brightness-200" src="../assets/icons/defuser.svg">
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="getTimelineEvents().explodeEvent && showRoundInfo">
                  <div
                    class="h-full border-red-500 absolute flex justify-end"
                    :style="[{ width: `${getTickTime(getTimelineEvents().explodeEvent)}%` }]"
                  >
                    <div class="z-25 flex h-full items-center justify border-r border-danger-500 border-dashed">
                      <div class="h-6 w-6 bg-danger-500 translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900">
                        <img class="brightness-200 p-1" src="../assets/icons/explosion.svg">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full flex bg-gray-950 h-full flex-1 items-center flex-row">
            <div
              class="h-full items-center flex justify-center text-center border-gray-700 cursor-pointer flex-1 border-b-4 -pb-4"
              :class="[
                roundWinner(index) === 2 ? 'text-[#e38617] border-b-[#e38617]' : 'text-[#687cca] border-b-[#687cca]',
                currentRound == index ? 'bg-gray-800' : 'border-b-gray-950 border-b-transparent',
              ]"
              @click="selectRound(index)"
              v-for="index in totalRounds"
              :key="index"
            >
              {{ index }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
</template>

<script setup>
import Dashboard from "../components/layouts/DashboardLayout.vue";
import { watch, ref, getCurrentInstance, onBeforeMount, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import protobuf from "protobufjs";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import { load } from "protobufjs";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { HandRaisedIcon, PencilIcon, TrashIcon } from "@heroicons/vue/20/solid";
import { Switch } from "@headlessui/vue";
import { useHead } from '@unhead/vue'
import { decompressGzipFromUrl } from '../utils/compression_util';

const store = useStore();
const stageParent = ref();
const stage = ref();
const layer = ref();
const group = ref();
const router = useRouter();
const map = ref();
const circle = ref();
const mapImage = ref();
const stageConfig = ref();
const mapImageConfig = ref();
const currentRound = ref(1);
const currentTick = ref();
const currentFrame = ref(0);
const playDemo = ref(false);
const intervalId = ref();
const ctPlayers = ref([]);
const tPlayers = ref([]);
const frameRate = ref(64);
const fires = ref([]);
const sliderFrame = ref(0);
const selectedPlayer = ref(null);
const roundData = ref(null);
const bomb = ref(null);
const totalRounds = ref(24);
const roundInfo = ref(null);
const mapInfo = ref(null);
const sliderActive = ref(false);
const activeKills = ref([]);
const bombPlantEvent = ref(null);
const roundEnded = ref(false);
const showLowerMap = ref(false);
const websocket = ref(null);
const subscription = ref(null);
const demoStatus = ref('ready');
const roundStatus = ref(true);
const showGrenadeGroups = ref(true);
const showRoundInfo = ref(true);
const selectedGrenadeGroupId = ref(null);
const isDrawing = ref(false);
const lines = ref([]);
const currentLine = ref(null);
const route = useRoute();
const playerStates = ref({});
const bombState = ref(null);

if(route.query.round) {
  currentRound.value = parseInt(route.query.round)
}

useHead({
  title: () => `Refrag | Demo Viewer`,
})

function subscribeToDemo() {
  const instance = getCurrentInstance();

  // Accessing $cable from the instance
  const cable = instance.appContext.config.globalProperties.$cable;

  if (!websocket.value) {
    websocket.value = cable.connect();
  }
  if (!subscription.value) {
    subscription.value = websocket.value.subscriptions.create(
      { channel: "DemoChannel", id: route.params.id },
      {
        received: (data) => {
          demoStatus.value = data.status;
          if(data.status === 'ready') {
            loadDemoData();
            loadRoundData();
          }
        },
      }
    );
  }
}

function launchGrenade(grenade) {
  axios({
    url: `/cs_servers/${store.getters.activeServer.id}/exec_server_command`,
    method: "POST",
    data: {
      command: 'teleport_to_nade',
      value: `${grenade.throwerX} ${grenade.throwerY} ${grenade.throwerZ}, ${grenade.throwerViewY} ${grenade.throwerViewX}, ${parseFloat(grenade.grenadePower) || 1}, ${grenade.isDucked || 0}, ${grenade.grenadeSimMode}`,
      is_client_command: true,
    },
    headers: {
      "X-TEAM-ID": store.state.activeTeam.id,
    },
  });
}

async function decodeFromURL(url) {
  try {
    // Fetch the zip file using Axios
    if (import.meta.env.VITE_ENVIRONMENT !== 'local.') {
      const response = await axios.get(url, {
        responseType: "arraybuffer",
      });
      const protoSchemaURL = new URL("/demo.proto", window.location.href).href;
      const {data: protoSchema} = await axios.get(protoSchemaURL, {
        responseType: "text",
      });

      let buffer;
      try {
        buffer = await decompressGzipFromUrl(url);
      } catch (error) {
        buffer = response.data;
      }
      const root = protobuf.parse(protoSchema).root;
      const roundDataBuffer = root.lookupType("Round");
      roundData.value = roundDataBuffer.decode(new Uint8Array(buffer));
    } else {
      const buffer = await decompressGzipFromUrl('/round-7.buf');
      const protoSchemaURL = new URL("/demo.proto", window.location.href).href;
      const {data: protoSchema} = await axios.get(protoSchemaURL, {
        responseType: "text",
      });
      const root = protobuf.parse(protoSchema).root;
      const roundDataBuffer = root.lookupType("Round");
      roundData.value = roundDataBuffer.decode(new Uint8Array(buffer));
    }
    
    clearCanvas();
    loadDisplay();
    loadTick();
  } catch (error) {
    console.error("Error loading files:", error);
  }
}

async function loadRoundData() {
  clearRoundData();
  await axios({
    method: "get",
    url: `/demos/${route.params.id}/round/${currentRound.value}`,
    headers: {
      "X-TEAM-ID": store.state.activeTeam.id,
    },
  })
    .then(({ data }) => {
      if(demoStatus.value !== 'ready') return;
      decodeFromURL(data.url);
    })
    .catch((error) => {
      console.log(error);
    });
}

function clearRoundData() {
  roundData.value = null;
}

function toggleMaps() {
  if(!mapInfo.value.has_lower_map) return;

  showLowerMap.value = !showLowerMap.value;

  if(showLowerMap.value) {
    const image = new window.Image();
    image.src = `/maps/${mapInfo.value?.name}_lower_radar.svg`;
    image.onload = () => {
      // set image only when it is loaded
      mapImage.value = image;
    };
  } else {
    const image = new window.Image();
    image.src = `/maps/${mapInfo.value?.name}_radar.svg`;
    image.onload = () => {
      // set image only when it is loaded
      mapImage.value = image;
    };
  }
  group.value.getNode().removeChildren();
  loadTick();
}

function analyzeDemo() {
  axios({
    method: "post",
    url: `/demos/${route.params.id}/analyze`,
    headers: {
      "X-TEAM-ID": store.state.activeTeam.id,
    },
  })
    .then(({ data }) => {
      demoStatus.value = data.status;
      if(data.status === 'ready') {
        loadDemoData();
        loadRoundData();
      }
    })
    .catch((error) => {
      console.log(error);
    });
}

async function loadDemoData() {
  await axios({
    method: "get",
    url: `/demos/${route.params.id}`,
    headers: {
      "X-TEAM-ID": store.state.activeTeam.id,
    },
  })
    .then(({ data }) => {
      if(data.pov_locked) {
        router.push({
          name: "Demos",
        });
        return;
      }
      if(!data?.parsed_data) {
        demoStatus.value = data.status;
        if(data.status !== 'analyzing') analyzeDemo();
        return;
      }
      mapInfo.value = data?.cs_map;
      totalRounds.value = data.parsed_data?.total_rounds || 24;
      roundInfo.value = data.parsed_data?.round_info || {};
      demoStatus.value = data.status;
      loadRoundData();
    })
    .catch((error) => {
      console.log(error);
    });
}

function loadDisplay() {
  const image = new window.Image();
  image.src = `/maps/${mapInfo.value?.name}_radar.svg`;
  image.onload = () => {
    // set image only when it is loaded
    mapImage.value = image;
  };

  updateStageConfig(stageParent.value.offsetHeight, stageParent.value.offsetWidth);
  updateMapConfig();

  var scaleBy = 1.1;
  var minScale = 0.75;
  var maxScale = 5;
  stage.value.getNode().on("wheel", (e) => {
    // stop default scrolling
    e.evt.preventDefault();

    var oldScale = stage.value.getNode().scaleX();
    var pointer = stage.value.getNode().getPointerPosition();

    var mousePointTo = {
      x: (pointer.x - stage.value.getNode().x()) / oldScale,
      y: (pointer.y - stage.value.getNode().y()) / oldScale,
    };

    // how to scale? Zoom in? Or zoom out?
    let direction = -e.evt.deltaY > 0 ? 1 : -1;

    // when we zoom on trackpad, e.evt.ctrlKey is true
    // in that case lets revert direction
    if (e.evt.ctrlKey) {
      scaleBy = 1.01;
      direction = -direction;
    }

    var newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    newScale = Math.max(minScale, Math.min(maxScale, newScale));

    stage.value.getNode().scale({ x: newScale, y: newScale });

    var newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    stage.value.getNode().position(newPos);
  });
}

function getTimeOfTick(tick) {
  if(bombPlantEvent.value && bombPlantEvent.value.tick < tick && !roundEnded.value) {
    let secondsSincePlant = (tick - bombPlantEvent.value.tick) / 64;
    let secondsLeft = 40 - secondsSincePlant;
    return secondsLeft;
  }

  if(roundEnded.value) {
    let secondsSinceRoundEnd = (tick - roundData.value.endTick) / 64;
    let secondsLeft = 7 - secondsSinceRoundEnd;
    return secondsLeft;
  }

  let secondsSinceStart = (tick - roundData.value.freezeTimeEndTick) / 64;
  let secondsLeft = 115 - secondsSinceStart;
  return secondsLeft;
}

// starts the interval to increase frame
function togglePlay() {
  playDemo.value = !playDemo.value;
  clearInterval(intervalId.value);

  if (playDemo.value) {
    intervalId.value = setInterval(loadNextTick, 1000 / frameRate.value);
  }
}

function stopPlay() {
  playDemo.value = false;
  clearInterval(intervalId.value);
}

function updateFrameRate() {
  let wasPlaying = playDemo.value;
  group.value.getNode().removeChildren();
  stopPlay();
  if (frameRate.value * 2 > 256) {
    frameRate.value = 8;
  } else {
    increaseFrameRate();
  }
  loadTick();
  if(wasPlaying) togglePlay();
}

function increaseFrameRate() {
  if (frameRate.value * 2 > 256) {
    return;
  } else {
    let wasPlaying = playDemo.value;
    group.value.getNode().removeChildren();
    stopPlay();
    frameRate.value = frameRate.value * 2;
    loadTick();
    if(wasPlaying) togglePlay();
  }
}

function decreaseFrameRate() {
  if (frameRate.value / 2 < 8) {
    return;
  } else {
    let wasPlaying = playDemo.value;
    group.value.getNode().removeChildren();
    stopPlay();
    frameRate.value = frameRate.value / 2;
    loadTick();
    if(wasPlaying) togglePlay();
  }
}

//update the stage config - this makes the canvas slightly dynamic
function updateStageConfig(height, width) {
  stageConfig.value = {
    width: width,
    height: height,
  };
}

//the base map config, if you want to make the map bigger by default you can change the .6
function updateMapConfig() {
  let sideLength = stageParent.value.offsetWidth * 0.6;

  mapImageConfig.value = {
    width: sideLength,
    height: sideLength,
    x: stageParent.value.offsetWidth / 2 - sideLength / 2,
    y: stageParent.value.offsetHeight / 2 - sideLength / 2,
    image: mapImage,
  };
}

function clearCanvas() {
  group.value.getNode().destroyChildren();
  bombPlantEvent.value = null;
  currentFrame.value = 0;
  sliderFrame.value = 0;
  ctPlayers.value = [];
  tPlayers.value = [];
  clearPlayerStates();
  stopPlay();
}

//select round functionality
function selectRound(round) {
  clearCanvas();
  showLowerMap.value = false;
  history.pushState({}, window.location, "?round=" + round)
  currentRound.value = round;
  loadRoundData();
}

function copySucceeded() {
  store.commit("showMessage", {
    messageType: "success",
    messageText: "Position Copied Successfully",
  });
  setTimeout(() => {
    store.commit("hideMessage");
  }, 3000);
}

function roundWinner(roundNum) {
  if(!roundInfo.value) return null;
  return roundInfo.value.filter((round) => round.round_num === roundNum)[0]?.winning_team;
}

const sortedTPlayers = computed(() =>
  tPlayers.value.sort(function(a, b){
    if(a.playerName?.toLowerCase() < b.playerName?.toLowerCase()) { return -1; }
    if(a.playerName?.toLowerCase() > b.playerName?.toLowerCase()) { return 1; }
    return 0;
  })
)

const sortedCTPlayers = computed(() =>
  ctPlayers.value.sort(function(a, b){
    if(a.playerName?.toLowerCase() < b.playerName?.toLowerCase()) { return -1; }
    if(a.playerName?.toLowerCase() > b.playerName?.toLowerCase()) { return 1; }
    return 0;
  })
)

function getSortedInventory(inventory) {
  const grenadeOrder = ["C4", "Decoy Grenade", "Flashbang", "HE Grenade", "Molotov", "Incendiary Grenade", "Smoke Grenade"];

  return inventory.sort((a, b) => grenadeOrder.indexOf(a.weaponName) - grenadeOrder.indexOf(b.weaponName));
}

//push player data to array so it can be accessed outside of canvas
function pushPlayer(player, side) {
  if (side === "CT") {
    const i = ctPlayers.value.findIndex(
      (_element) => _element.steamID === player.steamID
    );
    if (i > -1) ctPlayers.value[i] = player;
    else ctPlayers.value.push(player);
  } else {
    const i = tPlayers.value.findIndex((_element) => _element.steamID === player.steamID);
    if (i > -1) tPlayers.value[i] = player;
    else tPlayers.value.push(player);
  }
}

//use to the the percentage of when nade was thrown
function getTickTime(event) {
  let eventTick = event.tick || event.throwTick;
  return (
    ((eventTick - roundData.value.freezeTimeEndTick) /
      (roundData.value.endOfficialTick - roundData.value.freezeTimeEndTick)) *
    100
  );
}

function getGrenadeGroups() {
  // get all grenade throws and group them if they are +- 5 ticks close together
  let grenadeGroups = [];
  let grenades = roundData.value["grenades"];
  let lastTick = 0;
  let currentGroup = [];
  for (let i = 0; i < grenades.length; i++) {
    if (lastTick === 0) {
      lastTick = grenades[i].throwTick;
      currentGroup.push(grenades[i]);
    } else {
      if (grenades[i].throwTick - lastTick < 2 * 64) {
        currentGroup.push(grenades[i]);
      } else {
        grenadeGroups.push(currentGroup);
        currentGroup = [];
        currentGroup.push(grenades[i]);
      }
      lastTick = grenades[i].throwTick;
    }
  }
  return grenadeGroups;
}

function selectGrenadeGroup(group) {
  if(selectedGrenadeGroupId.value === group[0].uniqueID) {
    selectedGrenadeGroupId.value = null;
    return;
  }
  selectedGrenadeGroupId.value = group[0].uniqueID;
}

function getGrenadeThrowerName(grenade) {
  let player;
  if (grenade.throwerSide === "CT") {
    player = ctPlayers.value.find((player) => player.steamID === grenade.throwerSteamID);
  } else {
    player = tPlayers.value.find((player) => player.steamID === grenade.throwerSteamID);
  }
  return player?.playerName;

}

function getTimelineEvents() {
  //events are kills, bomb planted, bomb defused, bomb exploded
  let events = {
    kills: [],
    grenadeGroups: getGrenadeGroups(),
  };
  let bombDefuseEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_defused");
  let bombPlantedEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_planted");
  let bombExplodedEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_exploded");
  events.defuseEvent = bombDefuseEvent;
  events.plantEvent = bombPlantedEvent;
  events.explodeEvent = bombExplodedEvent;
  events.kills.push(...roundData.value["kills"]);
  return events;
}

//returns true if a nade is thrown from a CT
function isCT(nade) {
  return nade.throwerSide === "CT";
}

//this converts any cords based on the map to the given percentage on the map (good for responsive design)
function getConvertedCords(x, y) {
  if (x === undefined || y === undefined) return null;
  
  let sideLength = stageParent.value.offsetWidth * 0.6;
  let topLeftCord = {
    x: stageParent.value.offsetWidth / 2 - sideLength / 2,
    y: stageParent.value.offsetHeight / 2 - sideLength / 2,
  };
  let bottomLeftCord = {
    x: stageParent.value.offsetWidth / 2 + sideLength / 2,
    y: stageParent.value.offsetHeight / 2 + sideLength / 2,
  };

  let xPercent = ((x - mapInfo.value.x_0) / mapInfo.value.scale) / (1024)
  let yPercent = ((y - mapInfo.value.y_0) / mapInfo.value.scale) / (1024)

  let xCord = (bottomLeftCord.x - topLeftCord.x) * xPercent + topLeftCord.x;
  let yCord = (bottomLeftCord.y - topLeftCord.y) * -yPercent + topLeftCord.y;

  return { x: xCord, y: yCord };
}

function getPrimary(player) {
  let primary;
  player.inventory.forEach((item) => {
    if (item.weaponClass === "Rifle") {
      primary = item;
    } else if (
      item.weaponClass !== "Pistols" &&
      item.weaponClass !== "Grenade" &&
      item.weaponClass !== "Equipment"
    ) {
      primary = item;
    } else if (item.weaponClass === "Pistols" && !primary) {
      primary = item;
    }
  })

  if(!primary) return player.inventory[0]

  return primary;
}

function getBombColor() {
  let bombDefuseEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_defused");
  let bombPlantedEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_planted");

  if(currentTick.value > bombDefuseEvent?.tick) {
    return '#14B8A6';
  } else if (currentTick.value > bombPlantedEvent?.tick) {
    return '#EF4444';
  }
  return '#ebaa5c';
}

function updateBomb() {
  const lastProcessedTick = Math.floor(currentTick.value / 8) * 8;
  const nextProcessedTick = lastProcessedTick + 8;
  const progress = (currentTick.value - lastProcessedTick) / 8;

  let lastBombEvent = roundData.value["bombEvents"]
    .slice()
    .reverse()
    .find((event) => event.tick <= currentTick.value);

  let currentBomb = roundData.value["frames"][currentFrame.value]["bomb"];
  let nextFrame = roundData.value["frames"].find(f => f.tick === nextProcessedTick);
  let nextBomb = nextFrame?.bomb;
  
  if(lastBombEvent?.event === "bomb_planted") bombPlantEvent.value = lastBombEvent;

  if (!currentBomb?.x || !currentBomb?.y) return;

  let currentCords = getConvertedCords(currentBomb.x, currentBomb.y);
  let nextCords = nextBomb && nextBomb.x !== undefined ? 
    getConvertedCords(nextBomb.x, nextBomb.y) : null;

  if (!currentCords) return;
  
  // Interpolate position
  let bombCords = nextCords ? {
    x: currentCords.x + (nextCords.x - currentCords.x) * progress,
    y: currentCords.y + (nextCords.y - currentCords.y) * progress,
    z: currentBomb.z + (nextBomb.z - currentBomb.z) * progress
  } : {
    x: currentCords.x,
    y: currentCords.y,
    z: currentBomb.z
  };

  // Rest of the bomb event handling remains the same
  if(lastBombEvent?.event === "bomb_defuse_start") {
    if (stage.value.getNode().find("#bomb_progress_bar").length > 0) {
      let progressBar = stage.value.getNode().find("#bomb_progress_bar")[0];
      let defusalTime;

      if(lastBombEvent.hasKit) {
        defusalTime = 5 * 64;
      } else {
        defusalTime = 10 * 64;
      }

      let defusalPercent = ((currentTick.value - lastBombEvent["tick"]) / defusalTime) * 100;
      let progressPoint = defusalPercent / 100;

      if(progressPoint > 1) {
        progressBar.opacity(0);
        bombImg.fill('#14B8A6');
        return;
      }

      progressBar.fillLinearGradientStartPoint({ x: 0, y: 0 });
      progressBar.fillLinearGradientEndPoint({ x: stageConfig.value.width / 50, y: 0 });
      progressBar.fillLinearGradientColorStops([0, '#14B8A6', progressPoint, '#14B8A6', progressPoint, 'white', 1, 'white']);

    } else {
      const progressBar = new Konva.Rect({
          x: bombCords.x,
          y: bombCords.y,
          width: stageConfig.value.width / 50,
          height: 4,
          cornerRadius: .25, // This makes the corners rounded
          offset: {
            x: stageConfig.value.width / 100,
            y: -stageConfig.value.width / 200,
          }
      });
      progressBar.id("bomb_progress_bar");

      layer.value.getNode().add(progressBar);
      group.value.getNode().add(progressBar);
    }
  } else {
    if (stage.value.getNode().find("#bomb_progress_bar").length > 0) {
      let progressBar = stage.value.getNode().find("#bomb_progress_bar")[0];
      progressBar.destroy();
    }
  }

  let bombPlantedEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_planted");
  let bombTimeLeft;
  let bombDefuseEvent = roundData.value["bombEvents"].find((event) => event.event === "bomb_defused");

  if(currentTick.value > bombPlantedEvent?.tick) {
    bombTimeLeft = ((2560 - (currentTick.value - bombPlantedEvent["tick"])) / 64)
  }

  if (stage.value.getNode().find("#bomb_img").length > 0) {
    let bombImg = stage.value.getNode().find("#bomb_img")[0];

    bombImg.opacity(getObjectOpacity(bombCords.z, 1));
    if(currentTick.value > bombDefuseEvent?.tick) {
      bombImg.fill('#14B8A6');
      return;
    } else if (currentTick.value > bombPlantedEvent?.tick && bombTimeLeft > 0) {
      bombImg.fill('#EF4444');
    }

    // Update position directly instead of using animation
    bombImg.position({
      x: bombCords.x,
      y: bombCords.y
    });

  } else {
    var bombImg = new Konva.Path({
      x: bombCords.x,
      y: bombCords.y,
      fill: getBombColor(),
      data: "M343 1428 c-46 -23 -49 -47 -43 -445 5 -371 5 -373 28 -395 l23 -23 655 0 656 0 19 24 c18 22 19 44 19 411 0 367 -1 389 -19 411 l-19 24 -649 2 c-445 1 -655 -1 -670 -9z m1235 -276 l-3 -187 -280 0 -280 0 -3 184 c-1 106 1 187 7 190 5 3 133 5 285 3 l276 -3 -2 -187z m-460 -294 l-3 -53 -38 -3 c-21 -2 -45 2 -53 9 -14 12 -20 80 -7 92 3 4 28 7 55 7 l49 0 -3 -52z m150 0 l-3 -53 -38 -3 c-21 -2 -45 2 -53 9 -14 12 -20 80 -7 92 3 4 28 7 55 7 l49 0 -3 -52z m150 0 l-3 -53 -38 -3 c-21 -2 -45 2 -53 9 -14 12 -20 80 -7 92 3 4 28 7 55 7 l49 0 -3 -52z m150 0 c-3 -50 -5 -53 -32 -56 -15 -2 -39 -1 -52 2 -21 6 -24 12 -24 50 0 23 3 46 7 49 3 4 28 7 55 7 l49 0 -3 -52z m-450 -156 l3 -53 -53 3 -53 3 -3 38 c-4 52 12 69 62 65 l41 -3 3 -53z m150 0 l3 -53 -53 3 -53 3 -3 38 c-4 52 12 69 62 65 l41 -3 3 -53z m150 0 l3 -53 -53 3 -53 3 -3 44 c-4 52 7 62 63 59 l40 -3 3 -53z m150 0 l3 -53 -53 3 -53 3 -3 44 c-4 52 7 62 63 59 l40 -3 3 -53z",
      scaleX: stageConfig.value.width / 150000,
      scaleY: stageConfig.value.width / 150000,
      opacity: getObjectOpacity(bombCords.z, 1),
      offset: {
        x: 1000,
        y: 1000,
      },
    });

    bombImg.id("bomb_img");
    layer.value.getNode().add(bombImg);
    group.value.getNode().add(bombImg);
  }
}

function updateKills(newTick, oldTick) {
  const killTriggerTick = oldTick;
  const killDuration = newTick / 64 * 8;

  activeKills.value = activeKills.value.filter(kill => kill.tick <= killDuration && kill.tick >= currentTick.value);

  let triggerKills = roundData.value["kills"].filter(kill => kill.tick < currentTick.value && currentTick.value / 64 < (kill.tick / 64) + 8);

  triggerKills.forEach((kill) => {
    if (activeKills.value.findIndex((activeKill) => activeKill.tick === kill.tick) === -1) {
      activeKills.value.push(kill);
    }
  });

  activeKills.value.sort((a, b) => b.tick - a.tick);
}

function updateDamages(newTick, oldTick) {
  let damages = roundData.value["damages"].filter(
    (damage) => damage.tick >= oldTick && damage.tick <= newTick
  );

  damages.forEach((damage) => {
    let cords = getConvertedCords(damage["victimX"], damage["victimY"]);

    let randomXOffest = Math.random() * (10 - 5) + 5;
    let randomYOffest = Math.random() * (10 - 5) + 5;

    var damageText = new Konva.Text({
      x: cords.x,
      y: cords.y,
      opacity: 0.6,
      text: damage["hpDamage"],
      fontSize: stageConfig.value.width / 100,
      align: "center",
      lineHeight: 0.5,
      fill: "white",
      offset: {
        y: randomYOffest,
        x: randomXOffest,
      },
    });

    layer.value.getNode().add(damageText);
    group.value.getNode().add(damageText);

    var anim = new Konva.Animation(function (frame) {
      var yDist = -5 * (frame.timeDiff / (2000 / frameRate.value));
      damageText.move({ y: yDist });
    }, layer.value.getNode());

    anim.start();
    setTimeout(function () {
      anim.stop();
      damageText.destroy();
    }, 2000 / frameRate.value);
  });
}

function getObjectOpacity(z, base) {
  if(!mapInfo.value.upper_base_z) return base;

  if ((z < mapInfo.value.upper_base_z && !showLowerMap.value) || (z > mapInfo.value.upper_base_z && showLowerMap.value)) {
    return base * 0.5;
  } else {
    return base
  }
}

function getFlashBrightnessColor(rgb, user) {
  let flashPercentage = (user.flashDurationTimeRemaining / 4.87)

  var r = Math.round((1 - flashPercentage) * rgb[0] + flashPercentage * 255);
  var g = Math.round((1 - flashPercentage) * rgb[1] + flashPercentage * 255);
  var b = Math.round((1 - flashPercentage) * rgb[2] + flashPercentage * 255);

  return `rgb(${r}, ${g}, ${b})`
}

function getPlayerColor(player) {
  if (player.side === "CT") {
    if(player.isBlinded) {
      return getFlashBrightnessColor([104, 124, 202], player)
    }
    return '#687cca';
  } else if (player.side === "T") {
    if(player.isBlinded) {
      return getFlashBrightnessColor([227, 134, 23], player)
    }
    return '#e38617';
  }
}

//update the player locations based on frame, will create a new player if no player exists
function updatePlayers() {
  const lastProcessedTick = Math.floor(currentTick.value / 8) * 8;
  const nextProcessedTick = lastProcessedTick + 8;
  const progress = (currentTick.value - lastProcessedTick) / 8;

  let frame = roundData.value["frames"][currentFrame.value];
  let nextFrame = roundData.value["frames"].find(f => f.tick === nextProcessedTick);
  currentTick.value = frame.tick;

  let players = frame.ct.players.concat(frame.t.players);

  players.forEach((user) => {
    if (!playerStates.value[user.steamID]) {
      playerStates.value[user.steamID] = {};
    }

    playerStates.value[user.steamID] = {
      ...playerStates.value[user.steamID],
      ...user
    };

    const currentUser = playerStates.value[user.steamID];
    const nextUser = nextFrame?.ct.players.concat(nextFrame?.t.players).find(p => p.steamID === user.steamID);

    pushPlayer(currentUser, currentUser.side);

    if (currentUser.x !== undefined && currentUser.y !== undefined) {
      let currentCords = getConvertedCords(currentUser.x, currentUser.y);
      let nextCords = nextUser && nextUser.x !== undefined ? 
        getConvertedCords(nextUser.x, nextUser.y) : null;

      if (!currentCords) return;

      // Interpolate position
      let cords = nextCords ? {
        x: currentCords.x + (nextCords.x - currentCords.x) * progress,
        y: currentCords.y + (nextCords.y - currentCords.y) * progress,
        z: currentUser.z + (nextUser.z - currentUser.z) * progress
      } : currentCords;

      if (stage.value.getNode().find("#" + currentUser.steamID).length > 0) {
        if(currentFrame.value === 0) return;

        var circle = stage.value.getNode().find("#" + currentUser.steamID)[0];
        var text = stage.value.getNode().find("#text_" + currentUser.steamID)[0];
        var rect = stage.value.getNode().find("#rect_" + currentUser.steamID)[0];
        var equipment = stage.value.getNode().find("#img_" + currentUser.steamID)[0];
        var triangle = stage.value.getNode().find("#triangle_" + currentUser.steamID)[0];

        // Update positions directly
        const position = { x: cords.x, y: cords.y };
        circle.position(position);
        text.position(position);
        rect.position(position);
        if (equipment) equipment.position(position);
        triangle.position(position);

        // Update rotation
        if (currentUser.viewX !== undefined) {
          let currentAngle = (-currentUser.viewX + (90 % 360) + 360) % 360;
          triangle.rotation(currentAngle);
        }

        // Update states and colors
        if (!currentUser.isAlive) {
          circle.zIndex(0);
          circle.opacity(0.3);
          rect.opacity(0);
          triangle.opacity(0);
          equipment?.opacity(0);
          text.opacity(0);
          circle.strokeWidth(0);
        } else if (currentUser.isBlinded) {
          circle.fill(getPlayerColor(currentUser));
          triangle.fill(getPlayerColor(currentUser));
        } else {
          circle.fill(getPlayerColor(currentUser));
          circle.opacity(getObjectOpacity(cords.z, 1));
          triangle.fill(getPlayerColor(currentUser))
          triangle.opacity(getObjectOpacity(cords.z, 1));
          rect.opacity(getObjectOpacity(cords.z, 0.6));
          equipment?.opacity(getObjectOpacity(cords.z, 0.4));
          text.opacity(getObjectOpacity(cords.z, 1));
        }

        // Update weapon
        if (currentUser.isAlive && currentUser.activeWeapon) {
          let lastFrame = currentFrame.value > 0 ? roundData.value.frames[currentFrame.value - 1] : null;
          let lastFrameUsers = lastFrame?.ct.players.concat(lastFrame?.t.players);
          let lastUser = lastFrameUsers.find(u => u.steamID === currentUser.steamID);
          
          if (!lastFrame || !lastUser || lastUser.activeWeapon !== currentUser.activeWeapon) {
            var imageObj = new Image();
            imageObj.src = `/img/icons/${getActiveWeapon(currentUser).friendlyName}.png`;
            equipment.image(imageObj);
            equipment.offsetX(equipment.width() / 2);
          }
        }
      } else {
        //if the player doesn't exist yet on the map, create all needed knova elements

        //player cone
        var circle = new Konva.Circle({
          x: cords.x,
          y: cords.y,
          fill: getPlayerColor(currentUser),
          opacity: currentUser["isAlive"] ? getObjectOpacity(currentUser.z, 1) : 0.3,
          radius: stageConfig.value.width / 200,
          zIndex: currentUser["isAlive"] ? 5 : 0,
        });

        let triangleSideLength = stageConfig.value.width / 240;

        var triangle = new Konva.Line({
          x: cords.x,
          y: cords.y,
          fill: getPlayerColor(currentUser),
          closed: true,
          tension: 0.1,
          opacity: currentUser["isAlive"] ? getObjectOpacity(currentUser.z, 1) : 0,
          zIndex: currentUser["isAlive"] ? 5 : 0,
          rotation: (-currentUser["viewX"] + (90 % 360) + 360) % 360,
          points: [triangleSideLength, triangleSideLength, 0, 0, -triangleSideLength, triangleSideLength, 0, triangleSideLength * .75],
          offset: {
            y: stageConfig.value.width / 110,
          }
        });

        //player name
        var text = new Konva.Text({
          x: cords.x,
          y: cords.y,
          text: currentUser.playerName,
          opacity: currentUser["isAlive"] ? getObjectOpacity(currentUser.z, 1) : 0,
          fontSize: stageConfig.value.width / 150,
          align: "right",
          padding: 2,
          fill: currentUser["side"] === "CT" ? "#687cca" : "#e38617",
          offset: {
            y: stageConfig.value.width / 50,
          },
        });

        //background for player name
        var rect = new Konva.Rect({
          x: cords.x,
          y: cords.y,
          zIndex: currentUser["isAlive"] ? 5 : 0,
          opacity: currentUser["isAlive"] ? getObjectOpacity(currentUser.z, 0.6) : 0,
          width: text.width(),
          height: text.height(),
          fill: "black",
          offset: {
            y: stageConfig.value.width / 50,
            x: text.width() / 2,
          },
        });

        if (currentUser["isAlive"] && currentUser.activeWeapon) {
          var imageObj = new Image();
          imageObj.src = `/img/icons/${getActiveWeapon(currentUser).friendlyName}.png`;
          var equipment = new Konva.Image({
            x: cords.x,
            y: cords.y,
            image: imageObj,
            scaleX: stageConfig.value.width / 5000,
            scaleY: stageConfig.value.width / 5000,
            opacity: getObjectOpacity(currentUser.z, 0.4),
            offset: {
              y: 100 + stageConfig.value.width / 40,
            },
          });
          equipment.offsetX(equipment.width() / 2);
          equipment.id("img_" + currentUser["steamID"].toString());
          layer.value.getNode().add(equipment);
          group.value.getNode().add(equipment);
        }

        text.offsetX(text.width() / 2);
        circle.id(currentUser["steamID"].toString());
        text.id("text_" + currentUser["steamID"].toString());
        rect.id("rect_" + currentUser["steamID"].toString());
        triangle.id("triangle_" + currentUser["steamID"].toString());

        layer.value.getNode().add(rect);
        group.value.getNode().add(rect);
        layer.value.getNode().add(text);
        group.value.getNode().add(text);
        layer.value.getNode().add(circle);
        group.value.getNode().add(circle);
        layer.value.getNode().add(triangle);
        group.value.getNode().add(triangle);
      }
    }
  });
}

function getActiveWeapon(player) {
  if(player.activeWeapon === "UNKNOWN") {
    return {
      friendlyName: "Knife",
      name: 'weapon_knife'
    };
  } else {
    return {
      friendlyName: player.activeWeapon,
      name: player.activeWeaponClass
    };
  }
}

//weapon fires are seperate actions outside of a frame so we need to create a standalone function
function updateWeaponFires(newTick, oldTick) {
  let tickFireEvents = roundData.value["weaponFires"].filter(
    (event) =>
      event.tick >= oldTick && 
      event.tick <= newTick && 
      event.weaponClass !== "Grenade" &&
      event.playerX !== undefined &&
      event.playerY !== undefined
  );

  tickFireEvents.forEach((event) => {
    let cords = getConvertedCords(event["playerX"], event["playerY"]);
    if (!cords) return;

    if (event["playerViewX"] === undefined) return;

    let angleInDegrees = -event["playerViewX"] + 90;
    let angleInRadians = (angleInDegrees * Math.PI) / 180;

    const xOffset = 10 * Math.sin(angleInRadians);
    const yOffset = 10 * Math.cos(angleInRadians);

    var circle = new Konva.Circle({
      x: cords.x + xOffset,
      y: cords.y - yOffset,
      radius: stageConfig.value.width / 150,
      fill: "orange",
      opacity: 0.3,
    });

    var rect = new Konva.Rect({
      x: cords.x,
      y: cords.y,
      width: stageConfig.value.width / 5000,
      height: stageConfig.value.width / 200,
      fill: "white",
      opacity: 0.5,
      rotation: angleInDegrees - 180,
    });

    layer.value.getNode().add(circle);
    group.value.getNode().add(circle);
    layer.value.getNode().add(rect);
    group.value.getNode().add(rect);

    rect.zIndex(0);
    circle.zIndex(0);

    var anim = new Konva.Animation(function (frame) {
      let heightDist = 70 * (frame.timeDiff / (1000 / frameRate.value / 4));
      var scale = Math.sin(frame.time / (1000 / (frameRate.value * 4)) + 1.3);

      circle.scale({ x: scale, y: scale });
      rect.height(heightDist + rect.height());
    }, layer.value.getNode());

    anim.start();
    setTimeout(function () {
      rect.destroy();
      circle.destroy();
    }, 1000 / (frameRate.value * 4));
  });
}

function updateInfernos() {
  let tickFireEvents = roundData.value["infernos"];

  tickFireEvents.forEach((event) => {
    var existingFire = stage.value.getNode().find("#fire_" + event["uniqueID"])[0];
    var existingFireTimer = stage.value
      .getNode()
      .find("#fireTimer_" + event["uniqueID"])[0];

    if (
      event["startTick"] <= currentTick.value &&
      event["endTick"] >= currentTick.value
    ) {
      let cords = [];
      event["fires"]
        .find((fire) => fire.tick === currentTick.value)
        ?.points.forEach((position) => {
          let tempCords = getConvertedCords(position.x, position.y);
          cords.push(tempCords.x, tempCords.y);
        });

      if (existingFire) {
        if (currentTick.value % 8 !== 0) return;
        existingFire.points(cords);
        existingFireTimer.text(Math.ceil((event["endTick"] - currentTick.value) / 64));
      } else if (cords.length > 0) {
        var poly = new Konva.Line({
          points: cords,
          fill: "red",
          stroke: "black",
          strokeWidth: 1,
          tension: .5,
          zIndex: 3,
          closed: true,
          opacity: getObjectOpacity(event.z, 0.5),
        });

        let textCords = getConvertedCords(event.x, event.y);
        var fireTimer = new Konva.Text({
          x: textCords.x,
          y: textCords.y,
          opacity: getObjectOpacity(event.z, 0.6),
          text: Math.ceil((event["endTick"] - currentTick.value) / 64),
          fontSize: stageConfig.value.width / 100,
          align: "center",
          lineHeight: 0.5,
          fill: "white",
        });

        layer.value.getNode().add(fireTimer);
        group.value.getNode().add(fireTimer);
        layer.value.getNode().add(poly);
        group.value.getNode().add(poly);
        poly.id("fire_" + event["uniqueID"].toString());
        fireTimer.id("fireTimer_" + event["uniqueID"].toString());

        poly.zIndex(0);
      }
    } else if (existingFire) {
      existingFireTimer.destroy();
      existingFire.destroy();
    }
  });
}

function addArchToNade(cords, completionPercentage, distance) {
  const multiplierBase = -2 * Math.pow(completionPercentage, 2) + 2 * completionPercentage;

  // The constant '1000' can be tweaked based on the desired curve
  const scaledDistance = distance / 4000;
  const multiplier = multiplierBase * scaledDistance;

  cords.x -= (50 * multiplier);

  return cords;
}

function calculateDistanceBetweenCoords(coords) {
    if(coords.length < 2) return 0;
    var xDistance = coords[coords.length-1]["x"] - coords[0]["x"];
    var yDistance = coords[coords.length-1]["y"] - coords[0]["y"];

    return Math.sqrt(xDistance * xDistance + yDistance * yDistance);
}

//does calculations to figure out where a nade starts, add an arch to nade if Z is changing, and displays the nade and trail
function updateNadeProjectiles(projectiles) {
  const lastProcessedTick = Math.floor(currentTick.value / 16) * 16;
  const nextProcessedTick = lastProcessedTick + 16;
  const progress = (currentTick.value - lastProcessedTick) / 16;

  projectiles.forEach((projectile) => {
    if (
      projectile["throwTick"] < currentTick.value &&
      projectile["destroyTick"] > currentTick.value
    ) {
      if (
        projectile["grenadeType"] === "weapon_smokegrenade" &&
        projectile["effectStartTick"] <= currentTick.value &&
        projectile["destroyTick"] >= currentTick.value
      ) {
        createSmokeEffect(projectile);
        return;
      }

      let cordsArr = [];
      let currentCords = null;
      let nextCords = null;
      let segments = [];
      let lastBounceTick = projectile.throwTick;

      projectile.bounces.forEach((bounce, index) => {
        segments.push(projectile.vectors.filter(vector => 
          vector.tick > lastBounceTick && 
          vector.tick <= bounce.tick && 
          vector.x !== undefined && 
          vector.y !== undefined
        ));
        lastBounceTick = bounce.tick;
      });

      segments.push(projectile.vectors.filter(vector => 
        vector.tick > lastBounceTick && 
        vector.tick < projectile.destroyTick &&
        vector.x !== undefined && 
        vector.y !== undefined
      ));

      segments.forEach((segment) => {
        const distance = calculateDistanceBetweenCoords(segment);

        segment.forEach((vector, index, self) => {
          let convertedCords = getConvertedCords(vector.x, vector.y);
          if (!convertedCords) return;
          
          convertedCords.z = vector.z;

          const completionPercentage = index / (self.length);
          convertedCords = addArchToNade(convertedCords, completionPercentage, distance);

          // Store coordinates for interpolation
          if (vector.tick === lastProcessedTick) {
            currentCords = convertedCords;
          }
          if (vector.tick === nextProcessedTick) {
            nextCords = convertedCords;
          }

          // Add all points up to the last processed tick to the line
          if (vector.tick <= lastProcessedTick) {
            cordsArr.push(convertedCords.x, convertedCords.y);
          }
        });
      });

      if (!currentCords || cordsArr.length === 0) return;

      // Calculate interpolated position for the nade
      const interpolatedCords = nextCords ? {
        x: currentCords.x + (nextCords.x - currentCords.x) * progress,
        y: currentCords.y + (nextCords.y - currentCords.y) * progress,
        z: currentCords.z + (nextCords.z - currentCords.z) * progress
      } : currentCords;

      if (stage.value.getNode().find("#" + projectile["uniqueID"]).length > 0) {
        var path = stage.value.getNode().find("#" + projectile["uniqueID"])[0];
        var line = stage.value.getNode().find("#line_" + projectile["uniqueID"])[0];

        // Update nade position
        path.x(interpolatedCords.x);
        path.y(interpolatedCords.y);
        
        // Update line with full path including interpolated point
        line.points([...cordsArr, interpolatedCords.x, interpolatedCords.y]);

      } else {
        var path = new Konva.Path({
          x: interpolatedCords.x,
          y: interpolatedCords.y,
          opacity: getObjectOpacity(interpolatedCords.z, 1),
          fill:
            getGrenadeFromGrenades(projectile["uniqueID"]).throwerSide === "CT"
              ? "#687cca"
              : "#e38617",
          data: getNadeSvgData(projectile["grenadeType"]),
          strokeWidth: 1,
          stroke: "black",
          scaleX: stageConfig.value.width / 2000,
          scaleY: stageConfig.value.width / 2000,
          offset: {
            x: 10,
            y: 10,
          }
        });

        let isStill = Math.abs(interpolatedCords.x - cordsArr[cordsArr.length - 4]) +
                      Math.abs(interpolatedCords.y - cordsArr[cordsArr.length - 3]) < 0.001;

        var line = new Konva.Line({
          points: [...cordsArr, interpolatedCords.x, interpolatedCords.y],
          stroke:
            getGrenadeFromGrenades(projectile["uniqueID"]).throwerSide === "CT"
              ? "#687cca"
              : "#e38617",
          strokeWidth: 1,
          lineJoin: "round",
          opacity: isStill ? 0 : getObjectOpacity(interpolatedCords.z, 1),
          dash: [4, 4],
        });

        path.id(projectile["uniqueID"].toString());
        line.id("line_" + projectile["uniqueID"].toString());

        layer.value.getNode().add(path);
        group.value.getNode().add(path);
        layer.value.getNode().add(line);
        group.value.getNode().add(line);
      }
    } else {
      removeNadeProjectiles(currentTick.value, currentTick.value - 8);
    }
  });
}

// create smoke circle when proj is stopped
function createSmokeEffect(projectile) {
  if (!projectile.grenadeX || !projectile.grenadeY) return;
  //This function is needed as the smoke lifetime is accurate to the projectile
  //not the smoke effect in the round (IDK WHY, TY CS2)
  let smoke = stage.value.getNode().find("#smoke_" + projectile["uniqueID"])[0];
  let smokeTimer = stage.value.getNode().find("#smokeTimer_" + projectile["uniqueID"])[0];


  if (projectile["destroyTick"] <= currentTick.value) {
    smoke?.destroy();
    smokeTimer?.destroy();
    return;
  }
  if (smoke) {
    if (currentTick.value % 8 !== 0) return;
    smokeTimer.text(Math.ceil((projectile["destroyTick"] - currentTick.value) / 64));
    return;
  }

  // remove nades if smoke is up
  stage.value
    .getNode()
    .find("#" + projectile["uniqueID"])[0]
    ?.destroy();
  stage.value
    .getNode()
    .find("#line_" + projectile["uniqueID"])[0]
    ?.destroy();

  let cords = getConvertedCords(projectile["grenadeX"], projectile["grenadeY"]);
  var circle = new Konva.Circle({
    x: cords.x,
    y: cords.y,
    zIndex: 10,
    radius: stageConfig.value.width / 70,
    fill: getProjectileColor(projectile),
    opacity: getObjectOpacity(projectile.grenadeZ, 0.4),
    stroke: "black",
    strokeWidth: 1,
  });

  var smokeTimerText = new Konva.Text({
    x: cords.x,
    y: cords.y,
    width: 50,
    opacity: getObjectOpacity(projectile.grenadeZ, 0.6),
    text: Math.ceil((projectile["destroyTick"] - currentTick.value) / 64),
    fontSize: stageConfig.value.width / 100,
    align: "center",
    lineHeight: 0.5,
    fill: "white",
    offset: {
      x: 25,
      y: 2,
    },
  });

  layer.value.getNode().add(smokeTimerText);
  group.value.getNode().add(smokeTimerText);
  layer.value.getNode().add(circle);
  group.value.getNode().add(circle);

  smokeTimerText.id("smokeTimer_" + projectile["uniqueID"].toString());
  circle.id("smoke_" + projectile["uniqueID"].toString());
}

//used for flashes and HE grenades, creates the circle of explosion
function playGrenadeAnimation(grenade, color) {
  let cords = getConvertedCords(
    grenade["vectors"][grenade["vectors"].length - 1].x,
    grenade["vectors"][grenade["vectors"].length - 1].y
  );

  var circle = new Konva.Circle({
    x: cords.x,
    y: cords.y,
    radius: stageConfig.value.width / 100,
    fill: color,
    opacity: 0.5,
    offset: {
      x: stageConfig.value.width / 200,
    }
  });

  layer.value.getNode().add(circle);
  group.value.getNode().add(circle);

  circle.zIndex(0);

  var anim = new Konva.Animation(function (frame) {
    var scale = Math.sin(frame.time / (1000 / frameRate.value) + 1.5);
    circle.scale({ x: scale, y: scale });
  }, layer.value.getNode());

  anim.start();
  setTimeout(function () {
    circle.destroy();
  }, 1000 / frameRate.value);
}

//link the grenades outside of the frame data to those inside the frame data
function getGrenadeFromGrenades(nadeID) {
  return roundData.value["grenades"].find((grenade) => grenade.uniqueID === nadeID);
}

//removes nades when the next frame doesnt have them
function removeNadeProjectiles(newTick, oldTick) {
  let nadeEvents = roundData.value["grenades"].filter(
    (event) => event.destroyTick >= oldTick && event.destroyTick <= newTick
  );

  nadeEvents.forEach((event) => {
    if (stage.value.getNode().find("#" + event["uniqueID"]).length > 0) {
      //Destroy the nade projectile when the destroy tick is inbetween the new and old tick
      stage.value
        .getNode()
        .find("#" + event["uniqueID"])[0]
        ?.destroy();
      stage.value
        .getNode()
        .find("#line_" + event["uniqueID"])[0]
        ?.destroy();

      //Plays HE and Flash animation on destroy of these nade types
      //Fires and smokes are handled seperatly as they function differenlty
      if (event["grenadeType"] === "weapon_hegrenade") {
        playGrenadeAnimation(event, "orange");
      } else if (event["grenadeType"] === "weapon_flashbang") {
        playGrenadeAnimation(event, "white");
      }
    }
    stage.value
      .getNode()
      .find("#smoke_" + event["uniqueID"])[0]
      ?.destroy();
    stage.value
      .getNode()
      .find("#smokeTimer_" + event["uniqueID"])[0]
      ?.destroy();
  });
}

function loadTick() {
  if (!roundData.value["frames"][currentFrame.value + 1]) {
    togglePlay();
  } else {
    updatePlayers();
    updateBomb();
    updateInfernos();
    updateNadeProjectiles(roundData.value["grenades"]);
  }
}

function loadNextTick() {
  currentFrame.value++;
  sliderFrame.value++;
  loadTick();
}

//long function that returns the nade svg data which is used in nade proj. may change how this is handled
function getNadeSvgData(nadeType) {
  switch (nadeType) {
    case "weapon_decoy":
      return "M21.4516 8.21445L19.4362 7.0551V9.42391L18.2268 10.5331V12.9006H17.7229V15.3209H18.2268V17.84H17.7229V20.259H18.2268V22.7781H17.7229V25.1957H18.2268V27.5144L19.4362 29.5799V31.2932C19.4362 31.7627 19.2015 32.0001 18.7293 32.0001H9.76053C9.29099 32.0001 9.05622 31.7627 9.05622 31.2932V29.5799L10.2129 27.5144V25.1957H10.7682V22.7781H10.2129V20.259H10.7682V17.84H10.2129V15.3209H10.7682V12.9006H10.2129V10.5331L9.0549 9.42391V6.19912C9.0549 5.72958 9.28967 5.49349 9.75921 5.49349H10.4648L11.9763 4.18378V2.16581C11.1692 2.06689 10.7669 1.68045 10.7669 1.00779C10.7669 0.57254 10.9172 0.285012 11.2206 0.151799C11.3881 0.0515602 11.7244 0.00012207 12.2283 0.00012207H17.7704L22.4579 7.51014V23.3822L20.0904 24.5415C19.7541 24.709 19.5022 24.6589 19.3347 24.3899C19.1659 24.0878 19.2331 23.8029 19.5352 23.5339C19.8042 23.2991 20.2078 23.03 20.7446 22.728L21.4516 22.3772V8.21445Z";
    case "weapon_smokegrenade":
      return "M14.2822 9.81912V9.3855L13.6062 8.43874V7.73573L13.3085 7.41116L10.6312 0.595254L10.3335 0.568313V0H9.71007V0.161642L9.03528 1.1084C9.00834 0.891597 8.84541 0.864657 8.62861 0.864657C8.62861 0.864657 4.16549 0.837716 3.32777 0.864657C2.49006 0.891597 2.6517 1.40603 2.62476 2.11033H2.46183V2.89416H3.67928V4.92367C3.67928 4.92367 3.40988 5.11225 3.38165 5.46504C3.37396 5.57537 3.37011 5.7229 3.37011 5.87043C0.904426 6.1347 0 6.79025 0 6.79025V30.3233C0 30.3233 1.39064 32 5.98076 32C9.80885 32 11.7935 30.431 11.7935 30.431V7.44195L13.5253 10.0616L13.5792 27.3457C13.3368 27.8063 13.1469 28.2656 13.3085 28.2656C13.4702 28.2656 13.9846 28.2656 13.9846 28.2656L14.4439 27.914V9.873L14.2822 9.81912ZM11.2534 25.4804C11.2534 25.4804 8.9814 26.1706 5.89737 26.2104C2.81334 26.2514 0.500321 25.4804 0.500321 25.4804V23.7768C0.500321 23.7768 2.75946 24.467 5.89737 24.4529C8.94291 24.44 11.2534 23.7768 11.2534 23.7768V25.4804Z";
    case "weapon_flashbang":
      return "M21.9715 8.21354L19.9552 7.05348V9.42336L18.7466 10.5324V12.8996H18.2439V15.3193H18.7466V17.8384H18.2439V20.2581H18.7466V22.7772H18.2439V25.1968H18.7466V27.5157L19.9552 29.5818V31.2944C19.9552 31.7658 19.7195 32.0001 19.2494 32.0001H10.2805C9.81047 32.0001 9.57349 31.7658 9.57349 31.2944V29.5818L10.7322 27.5157V25.1968H11.2874V22.7772H10.7336V20.2581H11.2887V17.8384H10.7336V15.3167H11.2887V12.897H10.7336V10.5297L9.5748 9.42074V6.19456C9.5748 5.72451 9.81178 5.49014 10.2818 5.49014H10.9863L12.4985 4.1795V2.16313C11.692 2.06362 11.29 1.67868 11.29 1.00831C11.29 0.572299 11.4406 0.284246 11.743 0.150695C11.9106 0.0498763 12.2471 0.00012207 12.7512 0.00012207H18.2923L22.9797 7.50781V23.3795L20.6111 24.5396C20.2746 24.7072 20.0219 24.6574 19.8556 24.3877C19.6867 24.0865 19.7535 23.8011 20.056 23.5327C20.3257 23.297 20.729 23.0273 21.2645 22.7261L21.9715 22.3726V8.21354Z";
    case "weapon_hegrenade":
      return "m19.62 6.8543v0.09256c0.0283 0.02957 0.0437 0.0617 0.0437 0.09256 0 0.02956 0.0154 0.06041 0.0463 0.09255v0.04499c0.0321 0.02957 0.0475 0.06171 0.0475 0.09256 0.0309 0.02957 0.0604 0.06042 0.0913 0.09127v0.04628c0 0.03085 0.0167 0.04499 0.0463 0.04499v0.09256c0.0321 0.02956 0.0462 0.0617 0.0462 0.09255 0 0.03086 0.0155 0.06171 0.0476 0.09385l0.045 0.04499v0.09127c0.0321 0.02957 0.0476 0.0617 0.0476 0.09127l0.0462 0.04756v0.045 0.09384c0.0309 0.03085 0.0618 0.05913 0.0926 0.09127 0 0.03085 0.0141 0.06042 0.0437 0.09384l0.0463 0.04628v0.09127c0.0308 0.02957 0.0476 0.0617 0.0476 0.09255v0.04628c0.0295 0 0.0449 0.01414 0.0449 0.04499v0.09128c0.0322 0.02956 0.0463 0.0617 0.0463 0.09255 0.0309 0.02957 0.0463 0.06042 0.0463 0.09256 0.0296 0 0.0463 0.01671 0.0463 0.04628 0.0295 0.02956 0.0475 0.06041 0.0475 0.09255 0.0296 0.03085 0.0437 0.06042 0.0437 0.09127h0.0913c0 0.03085 0.0309 0.04757 0.0939 0.04757 0.0308 0.02828 0.0604 0.05913 0.0912 0.08998 0.0296 0.03085 0.0476 0.06042 0.0476 0.09256 0.0283 0.03085 0.045 0.0617 0.045 0.09127v0.09384c0.0296 0.02828 0.0463 0.05913 0.0463 0.0887 0 0.03342 0.0167 0.0617 0.0462 0.09127v0.04885c0 0.02957 0.0142 0.05913 0.0463 0.08998v0.09385c0.0309 0.03081 0.0463 0.06041 0.0463 0.09131 0.0309 0 0.0476 0.0154 0.0476 0.0449v0.0476c0.0308 0.0296 0.0462 0.0591 0.0462 0.0913v0.0938l0.045 0.0463c0 0.0296 0.0142 0.0579 0.0476 0.09 0 0.0308 0.0154 0.063 0.0463 0.0925v0.0926c0.0321 0 0.0463 0.0141 0.0463 0.0463l0.0449 0.0475c0.0322 0.0296 0.0463 0.0592 0.0463 0.0913 0.0309 0.0334 0.0463 0.0617 0.0463 0.0913v0.0475c0.0296 0.0296 0.0463 0.0605 0.0463 0.0913 0.0295 0.0322 0.0475 0.063 0.0475 0.0926l0.0438 0.045c0.0321 0.0308 0.0462 0.0604 0.0462 0.09l0.0476 0.0488v0.0926c0.0296 0.0295 0.0437 0.0604 0.0437 0.0912l0.0476 0.0463c0 0.0283 0.0154 0.0604 0.0462 0.0926 0 0.0295 0.0142 0.063 0.0437 0.0912v0.0463c0.0335 0.0309 0.0489 0.0604 0.0489 0.0926l0.0437 0.0462c0.0321 0.0296 0.0476 0.0605 0.0476 0.0913 0 0.0309 0.0154 0.0463 0.045 0.0463v0.0925c0.0308 0.0309 0.0462 0.0605 0.0462 0.0926 0.0309 0.0296 0.0463 0.0617 0.0463 0.0926l0.0476 0.045c0.0295 0.0308 0.0463 0.0629 0.0463 0.0925l0.045 0.0463v0.0925l0.0449 0.045v0.0913c0 0.0309 0.0168 0.063 0.0476 0.0926v0.0938c0.0296 0 0.0463 0.0141 0.0463 0.0437 0.0308 0.0296 0.045 0.0617 0.045 0.0939v0.0449c0.0321 0.0309 0.063 0.0605 0.0925 0.0926l0.0476 0.0476v0.0912c0 0.0309 0.0154 0.0617 0.045 0.0913v0.0926c0.0308 0 0.0476 0.0154 0.0476 0.045v0.0938l0.0449 0.0476v0.0887l0.0463 0.0501c0 0.0283 0.0155 0.0578 0.045 0.0887 0 0.0296 0.0167 0.0604 0.0476 0.0913v0.0475 0.0926c0.0295 0.0296 0.0463 0.0617 0.0463 0.0913l0.0475 0.0475c0.0296 0.0283 0.0592 0.0604 0.0926 0.09v0.0476c0.0308 0.0308 0.045 0.0617 0.045 0.0925v0.0913l0.0462 0.0463c0.0309 0.0295 0.0463 0.063 0.0463 0.0912v0.0952l0.0476 0.045v0.045c0.0308 0.0308 0.0604 0.0617 0.0913 0.0938 0 0.0296 0.0154 0.0604 0.0462 0.09 0 0.0308 0.0155 0.063 0.045 0.0951 0 0.0283 0.0167 0.0463 0.0463 0.0463v0.09 0.0938l0.0913 0.0926c0.0321 0.0295 0.0617 0.0604 0.0925 0.09v0.0925c0.0309 0.0296 0.0617 0.063 0.0926 0.0951v0.0437c0.0296 0.0309 0.045 0.0618 0.045 0.0939 0.0321 0.0296 0.0476 0.0617 0.0476 0.0913v0.0462c0.0295 0.0309 0.0604 0.0476 0.0925 0.0476 0 0.0309 0.0154 0.0617 0.0463 0.0926 0 0.0295 0.0154 0.0604 0.0463 0.0912 0 0.0309 0.0141 0.0617 0.0462 0.0913v0.045 0.0938c0.0296 0.0296 0.0437 0.0605 0.0437 0.0913l0.0476 0.0476c0.0309 0 0.0604 0.0141 0.0926 0.0462v0.0913c0.0295 0.0322 0.045 0.0617 0.045 0.0926l0.0462 0.045v0.0475 0.0913 0.0476c0.0322 0 0.063 0.0141 0.0939 0.0437 0 0.0321 0.0141 0.0642 0.0437 0.0925 0.0308 0 0.0463 0.0142 0.0463 0.045 0 0.0296 0.0167 0.0617 0.0462 0.0913v0.0476 0.0912 0.0476 0.09 0.0925 0.0939c-0.0295 0-0.0462 0.0141-0.0462 0.0437v0.0925l-0.0463 0.0463v0.5528 0.6427 0.6441 0.6916 0.6903 0.644 0.4165 0.09 0.1851 0.0913 0.1362 0.0952 0.0912 0.0463 0.0913 0.0463 0.0912 0.0913l0.0463 0.0476v0.0912c0 0.0322 0.0167 0.0618 0.0462 0.0913v0.0476 0.0925 0.045 0.0913 0.0476 0.09 0.0938 0.09 0.0475 0.0926 0.0463 0.0925 0.0476 0.09 0.0925 0.0913 0.0476 0.0912l0.0463 0.0463v0.09l0.0463 0.0489v0.0912 0.0913 0.09 0.0488 0.0913 0.0476 0.0912 0.0476 0.0913 0.0912 0.0463 0.0926 0.0912 0.0463 0.0939 0.0475 0.09 0.0939 0.0462 0.0913 0.0926 0.0912 0.0913 0.0926 0.0925l0.045 0.0463c0 0.0296 0.0154 0.0617 0.0463 0.0925v0.0926 0.09 0.0925 0.0926 0.0913 0.045 0.0925 0.0926 0.0912 0.0926 0.0913 0.0951 0.045 0.0913 0.0938 0.0913 0.09 0.0912 0.0952c-0.0309 0-0.0463 0.0141-0.0463 0.045v0.0925c-0.0309 0.0283-0.045 0.0591-0.045 0.0926v0.0912 0.0926 0.0913 0.0475 0.09 0.0926 0.0912 0.0939 0.0912 0.0926 0.0437 0.0939 0.0938 0.09 0.0925 0.0939 0.0912 0.045 0.0913 0.0939 0.0925c0 0.0321-0.0167 0.0617-0.0463 0.0913v0.0925c0 0.0296-0.0167 0.0605-0.0463 0.0926l-0.0462 0.0463c0 0.0308-0.0155 0.0604-0.0463 0.09-0.0296 0.0308-0.0437 0.0617-0.0437 0.0925-0.0309 0.0322-0.0476 0.0617-0.0476 0.0926h-0.0475c0 0.0308-0.0142 0.0617-0.0463 0.0925h-0.0553c-0.0926 0.0322-0.1543 0.0617-0.1838 0.0913-0.0617 0-0.1222 0.0154-0.1851 0.045h-0.3227c-0.0617-0.0296-0.1208-0.045-0.1825-0.045-0.0309-0.0296-0.0772-0.0591-0.1376-0.0913 0-0.0308-0.0167-0.0617-0.0476-0.0925v-0.045-0.0926-0.0913-0.0925-0.0476l0.0476-0.045c0.0309-0.0295 0.0604-0.0617 0.0913-0.0925 0.0308-0.0296 0.0617-0.0592 0.0912-0.0913h0.1839c0.0308-0.0296 0.0617-0.0604 0.0912-0.0925v-0.2289-0.6903-0.6916-0.644-0.6441-0.6903-0.6903-0.6453-0.689-0.6891-0.6466-0.2301-0.1851-0.0925-0.0463-0.0913-0.0925-0.0913-0.1864-0.09-0.1401c-0.0282-0.0296-0.0449-0.0592-0.0449-0.0913-0.0309-0.0308-0.0605-0.0476-0.0926-0.0476v-0.0912-0.045-0.0939-0.0912-0.0463-0.0939-0.0899-0.0476-0.0913-0.0475-0.0926-0.0463-0.09-0.0925-0.0913-0.0475c-0.0296-0.0309-0.045-0.0592-0.045-0.0913v-0.0476c-0.0308-0.0295-0.0463-0.0604-0.0463-0.0925v-0.0463-0.09-0.0951-0.09-0.045-0.0926-0.0462-0.0913-0.0476-0.09-0.0938-0.0913-0.0475-0.0913-0.0527-0.0926-0.0462-0.09-0.0926c-0.0295-0.0308-0.0617-0.0604-0.0912-0.0913v-0.0475-0.0913-0.0476-0.0912-0.0463-0.0938-0.0913-0.0463c-0.0309-0.0283-0.0463-0.0604-0.0463-0.09-0.0309-0.0308-0.045-0.063-0.045-0.0925v-0.0926-0.0938-0.0913-0.0926l-0.0463-0.0449c-0.0308-0.0309-0.0476-0.063-0.0476-0.0926v-0.0913-0.0925-0.0913c-0.0308-0.0309-0.0462-0.0604-0.0462-0.0926v-0.0912c-0.0309 0-0.0463-0.0167-0.0463-0.0463 0-0.0321-0.0142-0.0617-0.045-0.0938v-0.0913c-0.0321 0-0.0617-0.0154-0.0926-0.0463v-0.0476-0.0912-0.0926-0.0913-0.045-0.0912-0.0939c-0.0321 0-0.0475-0.0141-0.0475-0.0462-0.0309 0-0.0463-0.0155-0.0463-0.0476 0-0.0296-0.0154-0.0604-0.0476-0.09 0-0.0308-0.0141-0.0604-0.045-0.0925v-0.0913c-0.0295 0-0.0462-0.0154-0.0462-0.0476 0-0.0295-0.0142-0.0617-0.045-0.0913-0.0309-0.0308-0.0476-0.0617-0.0476-0.0938-0.0296 0-0.045-0.0141-0.045-0.045v-0.045c-0.0308-0.0308-0.0475-0.0604-0.0475-0.0912-0.0296-0.0309-0.0605-0.063-0.0926-0.0926 0-0.0309-0.0141-0.0463-0.045-0.0463 0-0.0321-0.0167-0.0617-0.0463-0.0925v-0.0913c-0.0308-0.0321-0.0475-0.063-0.0475-0.0939v-0.0437l-0.0412-0.0462c0-0.0309-0.0141-0.0605-0.045-0.0913 0-0.0309-0.0167-0.063-0.0462-0.09 0-0.0347-0.0168-0.0476-0.0476-0.0476v-0.0925c-0.0296-0.0309-0.0463-0.0617-0.0463-0.0939-0.0308-0.0295-0.0463-0.0604-0.0463-0.0899h-0.045v-0.0939c0-0.0321-0.0141-0.0604-0.0475-0.0913v-0.0938c-0.0296-0.0296-0.0579-0.045-0.0926-0.045 0-0.0296-0.0141-0.0617-0.0437-0.09h-0.0938c-0.0296-0.0321-0.0746-0.063-0.1376-0.0951-0.0308-0.0283-0.0437-0.0604-0.0437-0.0913-0.0321 0-0.063-0.0141-0.0938-0.0462v-0.0913c-0.0309-0.0309-0.0463-0.0604-0.0463-0.0926l-0.0463-0.0475v-0.09c-0.0295 0-0.0591-0.018-0.0913-0.0476-0.0308-0.0296-0.0617-0.0604-0.0938-0.0913 0-0.0321-0.0154-0.0604-0.045-0.0925v-0.0476c0-0.0295-0.0154-0.0617-0.0463-0.0912 0-0.0309-0.0154-0.0605-0.0475-0.0887 0-0.0348-0.0155-0.0502-0.0463-0.0502v-0.0887c-0.0321 0-0.0463-0.0154-0.0463-0.0475-0.0295-0.0296-0.0617-0.0617-0.0913-0.0939v-0.0437c0-0.0296-0.0167-0.0617-0.0449-0.0925v-0.0913c-0.0309-0.0309-0.0618-0.0604-0.0926-0.0913l-0.0463-0.0476c-0.0308-0.0321-0.063-0.0617-0.0938-0.0925v-0.045-0.0938-0.0438c-0.0296-0.0308-0.0592-0.0617-0.0913-0.0938-0.0296-0.0296-0.0617-0.0617-0.0913-0.0925 0-0.0296-0.0167-0.0579-0.0462-0.0913l-0.0463-0.045c-0.0296-0.0309-0.0463-0.0617-0.0463-0.0926l-0.045-0.0462v-0.0926c-0.0308 0-0.0476-0.0167-0.0476-0.045-0.0308-0.0308-0.0604-0.063-0.0899-0.0926 0-0.0308-0.0168-0.0604-0.0463-0.0925-0.0322-0.0296-0.0463-0.0617-0.0463-0.0926l-0.0463-0.0462c-0.0308-0.0296-0.0475-0.0605-0.0475-0.0913-0.0296 0-0.0605-0.0142-0.0926-0.0463v-0.0925c-0.0308 0-0.0463-0.0155-0.0463-0.0463-0.0295-0.0283-0.0462-0.0617-0.0462-0.0913-0.0296-0.0321-0.0605-0.063-0.0913-0.0926 0-0.0308-0.0154-0.0462-0.0476-0.0462 0-0.0296-0.0154-0.0617-0.0463-0.0913-0.0295-0.0334-0.0604-0.063-0.0912-0.0926l-0.0463-0.0488c-0.0296-0.0296-0.0604-0.0591-0.0926-0.09l-0.0424-0.045c0-0.0283-0.0167-0.0591-0.0488-0.0925v-0.0913c-0.0296-0.0296-0.0605-0.0476-0.0913-0.0476 0-0.0295-0.0154-0.0578-0.0463-0.0912-0.0308-0.0309-0.0475-0.0617-0.0475-0.0913-0.0296 0-0.0463-0.0154-0.0463-0.0476-0.0296-0.0321-0.0617-0.0463-0.09-0.0463-0.0322-0.0308-0.0463-0.0617-0.0463-0.0925 0-0.0296-0.0167-0.0617-0.0463-0.0926-1.1672 0-2.3486 0.0167-3.5454 0.0463 0 0.0309-0.0154 0.0463-0.0463 0.0463 0 0.0296-0.0141 0.0604-0.045 0.0925v0.0463 0.0926 0.0925 0.045 0.0939 0.0912 0.0913 0.09 0.0938 0.0913 0.0476 0.0925 0.0913 0.0926 0.0925 0.0913 0.0925 0.0463 0.0926 0.0938 0.09 0.0926 0.0925 0.0913 0.0463 0.0925 0.0926 0.09 0.0938 0.0913 0.0462 0.0926h0.0913 0.0926c0.0912 0 0.1851 0.0141 0.2751 0.045 0 0.0308 0.0154 0.0476 0.0475 0.0476 0.1209 0.0282 0.2134 0.0437 0.2764 0.0437 0.0604 0 0.1221 0.0167 0.1838 0.0475h0.0926c0.1838 0.0296 0.3844 0.045 0.5978 0.045h0.2313c0.0283 0.0309 0.0605 0.045 0.0913 0.045 0.0309 0.0322 0.0604 0.0489 0.0913 0.0489 0.0617 0 0.1093 0.0154 0.1388 0.045h0.0476c0.1221 0.0308 0.2288 0.0475 0.3214 0.0475 0.0899 0 0.1684 0.0142 0.2301 0.0463 0.0295 0.0296 0.0617 0.0437 0.0912 0.0437h0.0926c0.0617 0 0.1067 0.0154 0.1375 0.0501h0.0926c0.0604 0.0283 0.1247 0.0412 0.1838 0.0412 0.0617 0 0.1401 0.0167 0.2314 0.0475 0.0296 0.0296 0.0604 0.0605 0.0926 0.0913 0.0604 0.0309 0.1067 0.0476 0.1362 0.0476h0.0939c0.0912 0 0.1812 0.0154 0.2738 0.0475 0.0321 0.0283 0.063 0.0605 0.0938 0.0913 0.0283 0.0309 0.0746 0.0617 0.1376 0.0926 0.0604 0.0282 0.1221 0.0604 0.1838 0.0899 0.0309 0 0.0784 0.0155 0.1388 0.0476 0.0296 0.0309 0.0772 0.0463 0.1389 0.0463 0.0591 0.0308 0.1208 0.0617 0.1825 0.09 0 0.0321 0.0322 0.063 0.0926 0.0925l0.0463 0.0476c0.0295 0.0296 0.0617 0.0604 0.0912 0.0913 0.0322 0.0295 0.0617 0.0604 0.0913 0.0925v0.045c0.063 0.0309 0.108 0.0617 0.1401 0.0939 0.0309 0.0295 0.0746 0.0449 0.1376 0.0449 0 0.0309 0.0295 0.0605 0.0912 0.0913 0 0.0309 0.0142 0.0617 0.0463 0.0939 0.0309 0 0.0617 0.0167 0.0926 0.0462 0.0308 0.0296 0.0462 0.0579 0.0462 0.09 0.0296 0.0334 0.0592 0.063 0.0913 0.0939 0.0309 0.0295 0.0617 0.0462 0.0926 0.0462 0.0295 0.0296 0.0604 0.0605 0.0912 0.09l0.0463 0.0476c0.0296 0.0308 0.0604 0.0604 0.0939 0.0913 0 0.0295 0.0128 0.0488 0.0437 0.0488 0 0.0296 0.0167 0.0604 0.0462 0.09 0.0309 0.0309 0.0476 0.0604 0.0476 0.0926 0.0296 0.0295 0.0437 0.0617 0.0437 0.0912l0.0476 0.0476c0.0308 0.0308 0.0463 0.0617 0.0463 0.0925l0.0437 0.0463v0.0913l0.0488 0.045v0.0913c0.0283 0.0295 0.0437 0.0617 0.0437 0.0912 0.0322 0.0309 0.0476 0.063 0.0476 0.0939v0.0462c0.0296 0.0309 0.045 0.063 0.045 0.0913v0.0476c0.0308 0.0295 0.063 0.0604 0.0925 0.09v0.0475c0 0.0309 0.0155 0.0604 0.0476 0.0913v0.0913 0.0925c0.0296 0 0.0463 0.0142 0.0463 0.045 0.0308 0.0296 0.045 0.0617 0.045 0.0913v0.0476c0.0308 0.0308 0.045 0.0604 0.045 0.0912v0.0476 0.09c0 0.0295 0.0167 0.063 0.0475 0.0925v0.0939 0.0437c0.0296 0.0321 0.0463 0.0617 0.0463 0.0925l0.045 0.0463v0.0913c0.0321 0 0.0463 0.0154 0.0463 0.0463v0.0899c0 0.0322 0.0167 0.063 0.0463 0.0939v0.0463c0 0.0295 0.0154 0.0617 0.0475 0.0925v0.0926 0.0437c0 0.0321 0.0154 0.0617 0.045 0.0938v0.0463c0.0309 0.0296 0.0476 0.0617 0.0476 0.0913 0.0308 0.0308 0.045 0.0617 0.045 0.0925l0.0462 0.045c0.0309 0.0309 0.045 0.0617 0.045 0.0926 0.0322 0.0308 0.0476 0.063 0.0476 0.0925v0.0926c0 0.0308 0.0154 0.0617 0.0463 0.0913v0.0925c0 0.0296 0.0141 0.063 0.0475 0.0939v0.045c0.0296 0.0308 0.045 0.0617 0.045 0.0925 0.0309 0.0309 0.0476 0.063 0.0476 0.0939v0.0437l0.045 0.0462v0.0926 0.0913 0.0925 0.045 0.0926c0.0296 0.0321 0.0463 0.0617 0.0463 0.0925v0.045l0.0462 0.0476v0.0912 0.0926 0.0913 0.0463 0.0912 0.0913 0.0926 0.0925 0.0926 0.0462 0.0926 0.0926 0.0899 0.0926 0.0926 0.0925 0.0463 0.0925 0.0926 0.0913 0.0912 0.0939 0.09 0.0462 0.0939 0.0913c-0.0308 0.0321-0.0462 0.0604-0.0462 0.0925-0.0309 0.0296-0.0463 0.0591-0.0463 0.09v0.0938 0.0926 0.045 0.0925 0.0913 0.0913c0 0.0308-0.0142 0.063-0.045 0.0925v0.0913c0 0.0309-0.0167 0.0591-0.0476 0.0926l-0.045 0.045v0.0925 0.0913l-0.0475 0.0476v0.0449c-0.0309 0.0296-0.0463 0.0617-0.0463 0.0926v0.0926 0.0899 0.0463c-0.0309 0.0296-0.0476 0.063-0.0476 0.0926-0.0295 0.0308-0.045 0.0617-0.045 0.0925l-0.0462 0.0476v0.0437c-0.0296 0.0309-0.045 0.0617-0.045 0.0926v0.0925c0 0.0296-0.0155 0.045-0.0476 0.045v0.0939c-0.0296 0.0295-0.045 0.0604-0.045 0.0912v0.09l-0.0925 0.0938c0 0.0296-0.0142 0.0618-0.0463 0.0926v0.0913c0 0.0308-0.0142 0.045-0.045 0.045 0 0.0321-0.0167 0.063-0.0463 0.0951 0 0.0296-0.0154 0.0617-0.0476 0.0926v0.0912l-0.0424 0.0476v0.045c-0.0283 0.0308-0.0617 0.0617-0.0912 0.0938v0.09c-0.0309 0-0.0476 0.0167-0.0476 0.0463v0.0925c-0.0296 0.0296-0.0463 0.0592-0.0463 0.09 0 0.0309-0.0154 0.0617-0.0463 0.0939v0.0475l-0.045 0.045c0 0.0296-0.0141 0.063-0.0475 0.0926v0.0912l-0.0437 0.0463c0 0.0283-0.0142 0.0617-0.0489 0.0926v0.09l-0.0437 0.0462c-0.0308 0.0296-0.0463 0.0617-0.0463 0.0913l-0.0475 0.0476c0 0.0295-0.0142 0.0604-0.0437 0.0925v0.0926l-0.0476 0.0463c0 0.0283-0.0141 0.0617-0.0463 0.0925 0 0.0296-0.0154 0.0592-0.0437 0.09 0 0.0296-0.018 0.0463-0.0475 0.0463-0.0309 0.0308-0.0463 0.063-0.0463 0.0925 0 0.0309-0.0154 0.0476-0.0463 0.0476 0 0.0283-0.0141 0.0591-0.0463 0.09 0 0.0308-0.0141 0.0476-0.045 0.0476 0 0.0295-0.0141 0.0604-0.0463 0.0899 0 0.0309-0.0154 0.063-0.0462 0.0926-0.0322 0.0309-0.0617 0.0617-0.0913 0.0926v0.0462c-0.0309 0.0309-0.0463 0.0605-0.0463 0.0926-0.0308 0.0308-0.0617 0.0463-0.0925 0.0463 0 0.0295-0.0155 0.0617-0.0463 0.0912 0 0.0322-0.0154 0.0489-0.0463 0.0489 0 0.0283-0.0154 0.0591-0.0463 0.09v0.0912c-0.0308 0.0322-0.063 0.063-0.0925 0.0939-0.0296 0-0.0604 0.0141-0.0913 0.0463-0.0308 0.0308-0.063 0.0604-0.0938 0.0899v0.045c-0.0296 0.0309-0.0592 0.0617-0.0913 0.0939 0 0.0321-0.0141 0.0475-0.0437 0.0475 0 0.0296-0.018 0.0605-0.0476 0.09 0 0.0309-0.0167 0.0604-0.0463 0.0926-0.0295 0.0308-0.0604 0.0617-0.0925 0.0913l-0.045 0.0475c-0.0309 0.0309-0.0476 0.0617-0.0476 0.0926-0.0604 0.0308-0.1067 0.045-0.1362 0.045-0.0322 0.0321-0.0463 0.0617-0.0463 0.0938l-0.0463 0.0437c-0.0617 0.0309-0.108 0.0604-0.1388 0.0913-0.0617 0.0321-0.1067 0.0617-0.1376 0.0938-0.0308 0.0309-0.0604 0.0617-0.0925 0.0913-0.0309 0-0.0617 0.0141-0.09 0.0476-0.0309 0.0282-0.0476 0.0591-0.0476 0.0912-0.0308 0.0296-0.0604 0.045-0.0912 0.045-0.0605 0.0309-0.1235 0.0617-0.1852 0.0913-0.0308 0-0.0758 0.0167-0.1388 0.0476-0.0617 0.0308-0.108 0.0604-0.1363 0.0925-0.0321 0.0309-0.0629 0.0604-0.0925 0.0926-0.0296 0.0321-0.0604 0.045-0.0913 0.045-0.063 0.0308-0.1093 0.063-0.1401 0.0925-0.0591 0.0309-0.1234 0.063-0.1838 0.0939-0.0604 0.0282-0.1067 0.045-0.1376 0.045 0 0.0308-0.0154 0.0604-0.045 0.0912l-0.0475 0.0463c-0.0605 0.0309-0.1067 0.0617-0.1363 0.0926-0.0308 0.0321-0.063 0.0462-0.0938 0.0462-0.0617 0.0309-0.1222 0.0476-0.1839 0.0476-0.0308 0.0296-0.0617 0.045-0.0925 0.045-0.0643 0-0.1067 0.0154-0.1376 0.0463-0.0912 0.0308-0.1529 0.063-0.1851 0.0925-0.0617 0.0309-0.108 0.0463-0.1388 0.0463-0.0604 0.0296-0.108 0.045-0.1363 0.045-0.0938 0-0.1697 0.0154-0.2314 0.0476h-0.0925c-0.0592 0.0295-0.1209 0.0462-0.1813 0.0462-0.0655 0.0309-0.126 0.0463-0.1877 0.0463-0.0591 0.0296-0.1067 0.0592-0.1362 0.0913-0.0604 0.0308-0.1222 0.0604-0.1851 0.0925l-0.0913 0.045h-0.2764c-0.0321 0.0309-0.0476 0.0617-0.0476 0.0926h-0.0899c-0.0605 0.0321-0.1235 0.0476-0.1852 0.0476-0.0321 0-0.0771 0.0141-0.1388 0.0437h-0.0913c-0.0308 0.0308-0.0771 0.045-0.1375 0.045-0.1221 0-0.2301 0.0167-0.3214 0.0475h-0.1375c-0.0309 0.0322-0.0784 0.0463-0.1376 0.0463-0.0617 0-0.1234 0.0154-0.1851 0.0476h-0.0476c-0.0591 0.0295-0.1195 0.045-0.1812 0.045s-0.1401 0.0154-0.2314 0.045c-0.0617 0.0295-0.1067 0.0475-0.1376 0.0475-0.0308 0.0296-0.0771 0.0437-0.1388 0.0437h-0.4139c-0.0617 0.0309-0.1221 0.0604-0.1838 0.0926-0.0617 0.0308-0.108 0.0475-0.1389 0.0475-0.0295 0.0309-0.0771 0.0476-0.1375 0.0476-0.0926 0-0.1993 0.0154-0.3214 0.0463h-0.0463c-0.0295 0.0295-0.0771 0.0463-0.1388 0.0463h-2.071c-0.0629 0-0.1234-0.0168-0.18507-0.0463h-0.04371c-0.03085-0.0309-0.06299-0.0463-0.09384-0.0463-0.09256-0.0308-0.1684-0.0476-0.23139-0.0476-0.05913-0.0308-0.12212-0.0604-0.18383-0.0925 0-0.0309-0.01542-0.0476-0.04628-0.0476h-0.0437c-0.06299-0.0283-0.13755-0.0437-0.23139-0.0437-0.03086-0.0308-0.07713-0.0617-0.13627-0.0925l-0.04756-0.045c-0.06042-0.0309-0.10798-0.0476-0.13755-0.0476-0.09255-0.0296-0.18511-0.0463-0.27767-0.0463l-0.09127-0.0475h-0.04628c-0.09384-0.0296-0.16711-0.045-0.22882-0.045-0.06298-0.0296-0.12469-0.0592-0.18511-0.0913-0.0617-0.0309-0.12212-0.0617-0.18382-0.0926h-0.13884c-0.09256-0.0308-0.18383-0.0604-0.2751-0.0925-0.02956-0.0309-0.06299-0.0617-0.09127-0.0926h-0.04627c-0.09256-0.0334-0.15555-0.0475-0.18512-0.0475-0.09255-0.0296-0.18511-0.0617-0.27509-0.0926-0.03214 0-0.06557-0.0154-0.09385-0.0476h-0.04627c-0.09256-0.0295-0.15298-0.0449-0.18383-0.0449-0.0617-0.0296-0.13755-0.0618-0.23011-0.0926-0.02956-0.0296-0.07713-0.0591-0.13883-0.0913-0.06042 0-0.12084-0.0154-0.18254-0.045-0.06171-0.0308-0.13884-0.0617-0.23268-0.0938-0.02956-0.0309-0.04242-0.0617-0.04242-0.0926-0.06299-0.0295-0.12469-0.0462-0.1864-0.0462-0.03085-0.0296-0.0617-0.0592-0.09255-0.0913-0.03085 0-0.06171-0.0167-0.09256-0.045-0.02828-0.0309-0.05913-0.0617-0.08998-0.0938-0.06171-0.0296-0.09256-0.0605-0.09256-0.0926-0.03085 0-0.0617-0.0141-0.09256-0.045-0.02956-0.0308-0.0617-0.0604-0.09255-0.0926-0.02828-0.0321-0.05785-0.0617-0.09127-0.0925-0.03214-0.0296-0.07585-0.0476-0.13755-0.0476-0.03085-0.0295-0.07713-0.0591-0.13627-0.0912-0.03085 0-0.04756-0.0155-0.04756-0.045-0.03085-0.0309-0.0617-0.063-0.09127-0.0913-0.03085-0.0334-0.06299-0.0476-0.09384-0.0476 0-0.0295-0.01543-0.0604-0.04499-0.0912-0.03086-0.0322-0.04757-0.0617-0.04757-0.0939-0.03085-0.0308-0.06042-0.0604-0.09127-0.0913-0.02956-0.0295-0.06042-0.0437-0.09127-0.0437-0.03085-0.0321-0.0617-0.0617-0.09127-0.0938-0.02957 0-0.04756-0.0141-0.04756-0.045-0.06042-0.0296-0.12213-0.0617-0.18383-0.0926 0-0.0308-0.01543-0.0475-0.04371-0.0475-0.03085-0.0296-0.04885-0.0604-0.04885-0.0913-0.03085-0.0321-0.06041-0.0617-0.09255-0.0925-0.02957-0.0296-0.06042-0.0605-0.09256-0.09-0.02828 0-0.05913-0.0155-0.08998-0.0476 0-0.0321-0.01671-0.0617-0.04628-0.0938-0.02957 0-0.06171-0.0142-0.09256-0.045 0-0.0296-0.01542-0.0592-0.04628-0.09 0-0.0321-0.01542-0.0463-0.04627-0.0463 0-0.0296-0.01543-0.0617-0.04628-0.0938-0.02957-0.0296-0.04499-0.0592-0.04499-0.0913-0.03086-0.0309-0.06171-0.0617-0.09385-0.09-0.02956 0-0.06041-0.0154-0.09127-0.0488-0.03085-0.0296-0.04756-0.0605-0.04756-0.0913-0.03085 0-0.04628-0.0154-0.04628-0.0463v-0.0925l-0.04499-0.0463v-0.0926c-0.02957-0.0295-0.06299-0.0617-0.09256-0.0925 0-0.0296-0.01542-0.0604-0.04627-0.09-0.02957 0-0.04628-0.0167-0.04628-0.0476-0.03085-0.0295-0.045-0.0617-0.045-0.09l-0.04627-0.0475c0-0.0296-0.01671-0.0604-0.04757-0.0926l-0.09127-0.09c-0.03085-0.0308-0.06042-0.063-0.09127-0.0925 0-0.0309-0.01671-0.0617-0.04628-0.0926v-0.045-0.0925c0-0.0309-0.01671-0.0604-0.04627-0.0926 0-0.0296-0.01543-0.0617-0.04628-0.0913-0.03085 0-0.04628-0.0141-0.04628-0.0462-0.03085 0-0.06299-0.0155-0.09127-0.045v-0.0926c0-0.0308-0.01543-0.0604-0.04756-0.0913v-0.0462c0-0.0309-0.01415-0.0617-0.045-0.0939 0-0.0295-0.01542-0.0604-0.04756-0.09 0-0.0308-0.01543-0.0475-0.04499-0.0475v-0.0939-0.0462c0-0.0296-0.01543-0.0617-0.04628-0.0913v-0.0926c-0.03214 0-0.04756-0.0141-0.04756-0.0462 0-0.0283-0.01415-0.0592-0.045-0.0913v-0.0913c0-0.0321-0.01671-0.0475-0.04756-0.0475v-0.0913c-0.02957 0-0.04499-0.0167-0.04499-0.0476v-0.0913l-0.04628-0.0488v-0.09c0-0.0308-0.01543-0.0604-0.04628-0.0913 0-0.0308-0.01543-0.0617-0.04499-0.0912l-0.04628-0.0489v-0.09-0.0462c0-0.0296-0.01671-0.0617-0.04756-0.0913v-0.0476-0.0912-0.0926-0.09c-0.02829 0-0.04371-0.018-0.04371-0.0476-0.03085-0.0308-0.06171-0.0617-0.09256-0.0925v-0.0463-0.0925-0.0476-0.09-0.0938-0.09-0.0476c-0.02956-0.0308-0.04628-0.0617-0.04628-0.0913v-0.0449c-0.030847-0.0309-0.046273-0.0617-0.046273-0.0926v-0.0476-0.0912c0-0.0296-0.016712-0.0592-0.047564-0.0913v-0.0476c0-0.0295-0.015426-0.0591-0.046278-0.0912v-0.0913-0.0463-0.0913-0.0462-0.0913-0.0951-0.0437-0.0926-0.0913-0.0938-0.0913-0.09-0.0938-0.045-0.0913-0.0925-0.0926-0.0913-0.0938-0.0926-0.0462-0.09-0.0926-0.0925-0.0913-0.0938-0.0926-0.0476-0.0912-0.0913-0.0913-0.0925-0.0926-0.0925-0.045c0.030852-0.0309 0.046278-0.0605 0.046278-0.0926 0-0.0296 0.015426-0.0604 0.047564-0.0925v-0.0913-0.0913-0.0925-0.0463-0.09-0.0938c0-0.0296 0.015426-0.0618 0.046273-0.0926v-0.045c0.03086 0 0.04628-0.0167 0.04628-0.0476v-0.0912-0.0913-0.0476-0.0925-0.0926c0.03085-0.0295 0.06171-0.0604 0.09256-0.0925h0.04628v-0.09-0.0926-0.0925-0.0463-0.0913c0-0.0308 0.01671-0.063 0.04756-0.0925v-0.0926-0.0925-0.0913c0.02957-0.0296 0.04628-0.0604 0.04628-0.0926 0.02957 0 0.04499-0.0167 0.04499-0.045 0.03085-0.0321 0.06042-0.063 0.09256-0.0925v-0.0926c0-0.0295 0.01542-0.0437 0.04499-0.0437v-0.0463-0.0938-0.0913c0.02957-0.0295 0.04756-0.063 0.04756-0.0938v-0.0463c0.02957-0.0296 0.05914-0.0604 0.09127-0.0926 0-0.0295 0.01672-0.0617 0.04628-0.0912v-0.045c0.02957 0 0.04499-0.0142 0.04499-0.045 0.03086-0.0334 0.04757-0.063 0.04757-0.0913 0.03085-0.0334 0.04499-0.063 0.04499-0.0938v-0.0463-0.0926c0.03214-0.0308 0.04756-0.0604 0.04756-0.0925v-0.0913-0.0463c0.02829 0 0.04628-0.0154 0.04628-0.0475 0.02957-0.0296 0.045-0.0617 0.045-0.0913 0.03085-0.0308 0.06298-0.0617 0.09255-0.0938l0.04628-0.045v-0.0913c0.02957-0.0309 0.04628-0.0617 0.04628-0.0913 0.03085-0.0308 0.06042-0.0617 0.09127-0.0925l0.09127-0.0862c0.03085-0.0295 0.04756-0.0604 0.04756-0.0912 0.02957-0.0322 0.04628-0.063 0.04628-0.0939l0.04499-0.0437c0.03085-0.0321 0.04628-0.0655 0.04628-0.0951 0.03085-0.0296 0.04628-0.0604 0.04628-0.0926 0.02957-0.0295 0.06299-0.0591 0.09256-0.0899 0.02956 0 0.04499-0.0168 0.04499-0.0463 0.03085 0 0.06299-0.0167 0.09384-0.0463 0.02957-0.0309 0.06042-0.0617 0.09127-0.0938 0.03085-0.0296 0.06299-0.0605 0.09384-0.09l0.04499-0.0463c0.03086-0.0321 0.04628-0.063 0.04628-0.0951 0.03085-0.0283 0.06299-0.0592 0.09256-0.09 0.03085-0.0309 0.06299-0.0617 0.09255-0.0939 0.02957 0 0.04628-0.0154 0.04628-0.0449 0.03085 0 0.06171-0.0168 0.08999-0.045 0.03085-0.0322 0.0617-0.063 0.09255-0.0952 0.03086-0.0282 0.06171-0.0604 0.09256-0.0912l0.04628-0.0425c0.02828-0.0295 0.04371-0.0617 0.04371-0.0912 0.03213-0.0309 0.0617-0.0604 0.09127-0.0926 0.03085-0.0308 0.06299-0.0476 0.09255-0.0476 0-0.0295 0.018-0.0617 0.04757-0.0899 0.06041 0 0.12212-0.018 0.18254-0.0476 0.03085-0.0296 0.06042-0.0604 0.09127-0.0913 0.03214-0.0321 0.07841-0.0604 0.14012-0.0925h0.13755 0.13883c0.02957-0.0296 0.05913-0.0604 0.08999-0.0926 0.06041-0.0295 0.12597-0.0617 0.18382-0.0938 0.09256-0.0296 0.15555-0.0592 0.1864-0.0913 0.06042 0 0.12341-0.0128 0.18254-0.0437 0.06171-0.0321 0.13884-0.0463 0.23139-0.0463 0.03085 0 0.06171-0.0154 0.09256-0.0475h0.04756c0.06042-0.0296 0.11955-0.045 0.18126-0.045 0.06042-0.0322 0.10798-0.0489 0.13883-0.0489 0.06042-0.0296 0.12084-0.045 0.18254-0.045h0.13884c0.0617 0 0.12212-0.0154 0.18382-0.045h0.04628c0.06171-0.0308 0.12212-0.0475 0.18383-0.0475 0.12341-0.0296 0.26096-0.0437 0.41393-0.0437 0.03214-0.0322 0.07842-0.063 0.14012-0.0926 0.05913 0 0.12212-0.0154 0.18383-0.0463 0.02956-0.0321 0.06042-0.0475 0.09127-0.0475 0.0617 0 0.10798-0.0168 0.13883-0.0463l0.04628-0.0463h0.18383c0.27766 0 0.56819-0.0141 0.87414-0.045h0.09255 0.13884v-0.045h0.45764v-0.1362-0.0926-0.0926-0.0462-0.0913-0.0926-0.0925-0.09-0.0938-0.0926-0.0463-0.0925-0.0913-0.0926-0.0925-0.0913-0.0925-0.0476-0.0913-0.0938c-0.02957-0.0296-0.04628-0.0592-0.04628-0.09v-0.0913-0.0912-0.0939-0.045-0.0925-0.0926-0.0463-0.0925-0.0463h-0.36765c0-0.0296-0.01672-0.0604-0.045-0.0913v-0.09-0.0938h-0.13883v-0.0913-0.0925-0.0913-0.09384-0.13884-0.09127-0.0887-0.41521-0.09127-0.09256-0.04628-0.09255-0.09256-0.09127-0.09127-0.09256-0.09127-0.04628-0.09384-0.09127-0.09384-0.09127-0.09127-0.09127-0.04499-0.09385-0.09255-0.09256-0.09127-0.09127-0.09256-0.04499-0.09255-0.09256-0.09256-0.09127-0.09255-0.09127-0.04628-0.09256-0.09255-0.04371-0.09513-0.04499-0.09256-0.04628-0.09127-0.09255-0.09256-0.04628-0.09255-0.04628-0.09256-0.04499-0.09255-0.09256-0.09127-0.04628-0.09127-0.04628-0.09256-0.0437-0.09256-0.09127-0.09256-0.04756-0.09127-0.04628-0.09255-0.045c-0.30724 0-0.59905 0.01671-0.87543 0.045h-0.13755c-0.05784 0.03213-0.12083 0.04627-0.18254 0.04627h-0.04499-0.13755c-0.06299-0.02828-0.14012-0.04627-0.2301-0.04627-0.06171 0-0.1067-0.01414-0.14012-0.045-0.05914-0.03085-0.13755-0.06299-0.23011-0.09255-0.03085-0.02957-0.04628-0.06042-0.04628-0.08999-0.02956-0.03085-0.0617-0.06042-0.09127-0.09384-0.03214 0-0.04628-0.01542-0.04628-0.04628-0.03213-0.02956-0.04627-0.06042-0.04627-0.09255v-0.045c-0.03086-0.03085-0.045-0.06299-0.045-0.09127v-0.04628-0.09384-0.09127c0-0.02956 0.01414-0.04627 0.04628-0.04627v-0.09128c0-0.02956 0.01414-0.05913 0.04628-0.09255l0.04628-0.04628c0.02828-0.02828 0.06042-0.06042 0.08998-0.09127l0.04628-0.04628c0.0617-0.03085 0.12341-0.04756 0.18511-0.04756h0.045v-0.04371-0.09255-0.04757-0.08998-0.09513-0.04371l0.04756-0.04628c0.03085-0.02956 0.06042-0.06042 0.09256-0.09127 0.02956-0.02956 0.04627-0.0617 0.04627-0.09255 0.027-0.03214 0.06042-0.06171 0.08999-0.09384 0-0.02957 0.01542-0.04757 0.04628-0.04757 0.02956-0.02828 0.06042-0.05913 0.09127-0.08998 0.0617 0 0.10798-0.01414 0.13883-0.045l0.04628-0.04627h0.18383 0.64403v-0.04628c0.03085-0.03085 0.06171-0.04628 0.09384-0.04628 0.02829-0.02957 0.05914-0.06042 0.09128-0.09127 0.06041 0 0.10798-0.01414 0.13754-0.04499v-0.04628-0.09127h6.7669v-0.04757-0.09384-0.08998-0.09127-0.04628-0.09256-0.09255-0.09256-0.09127-0.092556-0.046279-0.089984-0.091271-0.048849c0.0321-0.030852 0.0784-0.046278 0.1388-0.046278v-0.044993h1.1968v0.044993 0.093841 0.091271 0.089985l0.0463 0.047564v0.092556c0.0309 0.029566 0.0463 0.059133 0.0463 0.09127l0.0463 0.04628v0.04628c0.0308 0.03085 0.0449 0.06041 0.0449 0.09255v0.09256c0 0.02828-0.0141 0.04628-0.0449 0.04628v0.09127c-0.0309 0.02956-0.0463 0.06042-0.0463 0.08998-0.0309 0.03085-0.0463 0.06171-0.0463 0.09384v0.09256 0.09256h0.1838c0.0322 0.02956 0.063 0.06042 0.0913 0.08998 0.0321 0.03214 0.0617 0.04628 0.0938 0.04628 0.0296 0.03085 0.0463 0.0617 0.0463 0.09384 0.0309 0 0.0617 0.01671 0.0926 0.04628 0.0308 0.03085 0.0463 0.06042 0.0463 0.09127l0.0475 0.04371c0.0296 0.03085 0.045 0.06427 0.045 0.09384 0.0309 0.03085 0.0476 0.0617 0.0476 0.09384v0.04628c0.0308 0.03085 0.045 0.0617 0.045 0.09127v0.04628 0.09127c0 0.02956-0.0142 0.06042-0.045 0.09127v0.04499h0.045c0 0.03085 0.0154 0.06299 0.045 0.09256v0.09127c0.0308 0.03085 0.0475 0.0617 0.0475 0.09384l0.045 0.04628v0.09255 0.09256c0.0309 0.03085 0.0476 0.0617 0.0476 0.09127h0.0463c0.0295 0.02957 0.0449 0.0617 0.0449 0.09127 0.0309 0 0.063 0.018 0.0926 0.04628 0.0309 0.03085 0.0463 0.06042 0.0463 0.09384l0.0475 0.04499c0.0296 0.02957 0.045 0.06171 0.045 0.09127l0.0476 0.04628v0.09127l0.045 0.04628v0.09384c0 0.02957 0.0154 0.06042 0.0476 0.08999v0.09255l0.0449 0.045v0.09255l0.045 0.04628v0.09127l0.0476 0.04756c0 0.03086 0.0154 0.06171 0.045 0.09256v0.09127c0.0309 0.02957 0.0604 0.06171 0.0926 0.09256 0.0308 0 0.0475 0.01671 0.0475 0.04499 0.0283 0.03214 0.0437 0.0617 0.0437 0.09256v0.04627 0.09128l0.0476 0.04627c0.0296 0.03086 0.0604 0.06299 0.0925 0.09127 0 0.02957 0.0142 0.06042 0.0463 0.09256 0 0.02957 0.0154 0.06042 0.045 0.09256v0.04499c0.0296 0.03085 0.0463 0.0617 0.0463 0.09255v0.04628c0.0308 0.03086 0.0463 0.06042 0.0463 0.09256 0.0283 0 0.0604 0.01542 0.09 0.04628v0.09255c0.0308 0.02957 0.0462 0.06042 0.0462 0.09256v0.09127c0.0309 0 0.0476 0.01671 0.0476 0.04628 0.0309 0.02828 0.0463 0.06042 0.0463 0.09255v0.045c0 0.02956 0.0141 0.06299 0.045 0.09512l0.0462 0.04371c0 0.03085 0.0155 0.06171 0.0489 0.09256 0 0.02828 0.0129 0.06042 0.0424 0.09255v0.04628c0.0322 0.02957 0.0489 0.06042 0.0489 0.09127 0.0295 0.02957 0.045 0.05914 0.045 0.09256l0.0462 0.04371c-0.0077 0.03085 9e-3 0.04627 0.0412 0.04627zm-17.357 13.349v-0.0913-0.0926-0.0463l-0.04628 0.0463v0.0926c-0.03085 0.0308-0.04499 0.0617-0.04499 0.0913v0.0912 0.0926 0.0925c0-0.0308 0.01542-0.063 0.04499-0.0925v-0.0926c0.03214 0 0.04628-0.0141 0.04628-0.045v-0.0462z";
    case "weapon_molotov":
      return "m17.657 7.2798c0.3195 0.11791 0.6697 0.30775 1.047 0.56597 0.3773 0.29242 0.6839 0.56833 0.915 0.83009 0.2028 0.26176 0.2771 0.45042 0.2193 0.56597l-1.3088 1.7887c-0.0295 0.0283-0.1026 0.0448-0.2193 0.0448-1.5989 2.6754-2.5445 4.2141-2.8346 4.6221-0.1167 0.1439-0.2476 0.9468-0.3938 2.3983-0.1733 1.4244-0.4787 2.4573-0.915 3.0963l-7.5875 10.644c-0.23228 0.2028-0.7558 0.2182-1.5706 0.0437-0.87135-0.2323-1.7156-0.6403-2.528-1.2228-0.81476-0.5789-1.4692-1.2498-1.9644-2.0045-0.43627-0.6992-0.59545-1.192-0.4799-1.4844l7.5899-10.685c0.43627-0.6414 1.3088-1.264 2.6176-1.876 0.3196-0.145 0.7558-0.349 1.3077-0.6107 0.4079-0.204 0.6685-0.3773 0.7864-0.5224 0.1745-0.2641 1.323-1.6578 3.4454-4.1893-0.0283-0.11555-0.0283-0.20163 0-0.26058h-0.0448l1.3088-1.7887c0.0566-0.11555 0.2606-0.09904 0.6096 0.04481z";
    case "weapon_incgrenade":
      return "M10.6795 0.888998L10.7272 0.936738V1.02964C10.7272 1.05802 10.7427 1.0748 10.7737 1.0748V1.16899H10.8201V1.21673C10.8498 1.21673 10.8666 1.23221 10.8666 1.26318V1.40382H10.913V1.49801H10.9595V1.5922C10.9904 1.5922 11.0059 1.60768 11.0059 1.63736V1.778L11.0524 1.82445V1.91993L11.0988 1.96637V2.01541H11.1466V2.10701L11.1917 2.15346V2.2012C11.2227 2.2012 11.2395 2.21669 11.2395 2.24765V2.29539L11.2859 2.34055V2.48119C11.2859 2.51087 11.3014 2.52764 11.3324 2.52764V2.57538H11.3788V2.71473H11.4253V2.76118L11.4717 2.80892V2.85408L11.5182 2.90182V2.99472L11.5659 3.04246V3.18439L11.6111 3.23213V3.27987L11.6575 3.32503V3.37277L11.7504 3.46567V3.51341C11.7801 3.51341 11.7969 3.5289 11.7969 3.55986L11.842 3.60631V3.84243L11.8885 3.88888V3.98178L11.9349 4.02952V4.07597L11.9814 4.12242V4.16887L12.0239 4.21661V4.3108H12.0704V4.4037C12.0704 4.43338 12.0872 4.45015 12.1168 4.45015V4.59079C12.1478 4.59079 12.1633 4.60756 12.1633 4.63724V4.73014C12.193 4.73014 12.2097 4.7482 12.2097 4.77788V4.87078H12.2575V4.91723L12.3026 4.96368V5.01142L12.3491 5.05916V5.10432L12.3955 5.15206V5.34044L12.442 5.38689V5.48108H12.4884V5.62043H12.5349V5.71333L12.5813 5.75978V5.80623L12.6278 5.85268V5.94687C12.6588 5.94687 12.6742 5.96364 12.6742 5.99332V6.08621C12.6742 6.11718 12.6897 6.13396 12.7207 6.13396V6.22557L12.7684 6.2746V6.32104H12.8123V6.3662L12.8588 6.41394V6.4591L12.9065 6.50813V6.60103H12.9517V6.74038L12.9994 6.78812V6.83457C12.9994 6.86425 13.0149 6.88231 13.0458 6.88231V6.92747L13.0923 6.97521V7.11456L13.1852 7.20746V7.30165L13.2316 7.3481V7.39584H13.2781V7.48745H13.3245C13.3245 7.51842 13.34 7.5339 13.371 7.5339V7.58293L13.5568 7.77002V8.38032L13.6045 8.42677V8.52096C13.6329 8.52096 13.6497 8.53644 13.6497 8.56741L13.6961 8.61257V8.65902L13.7426 8.70547V8.75321H13.7903V8.8474C13.82 8.8474 13.8368 8.86417 13.8368 8.89385L13.8832 8.93901V8.98675H13.9297V9.0332L14.0226 9.12868C14.0226 9.15706 14.0381 9.17384 14.0677 9.17384V9.22158H14.1155V9.26803L14.2548 9.45512V9.78414C14.2845 9.8151 14.3155 9.83059 14.3464 9.83059V10.4409C14.3464 10.566 14.3464 10.6589 14.3464 10.7222C14.3464 10.8164 14.3464 10.9093 14.3464 11.0034V11.2383C14.3464 11.3312 14.3464 11.4254 14.3464 11.5208V11.7544C14.3464 11.8795 14.3464 11.9892 14.3464 12.0834C14.3464 12.1453 14.3464 12.2382 14.3464 12.3647C14.3464 12.4266 14.3464 12.5208 14.3464 12.646C14.3464 12.7092 14.3464 12.7853 14.3464 12.8808V12.9272C14.3464 12.9905 14.3464 13.0692 14.3464 13.1621C14.3464 13.2253 14.3464 13.3195 14.3464 13.4433C14.3464 13.5053 14.3464 13.5995 14.3464 13.7259C14.3464 13.7879 14.3464 13.8666 14.3464 13.9607C14.3464 14.0549 14.3464 14.1478 14.3464 14.2407C14.3464 14.3349 14.3464 14.4291 14.3464 14.5233C14.3464 14.6175 14.3464 14.7117 14.3464 14.8046C14.3464 14.8988 14.3464 14.9775 14.3464 15.0394C14.3464 15.1659 14.3464 15.2588 14.3464 15.322C14.3464 15.4149 14.3464 15.5091 14.3464 15.602C14.3464 15.6962 14.3464 15.7904 14.3464 15.8845C14.3464 15.9787 14.3464 16.0574 14.3464 16.1207C14.3464 16.2445 14.3464 16.3374 14.3464 16.4007C14.3464 16.4936 14.3464 16.589 14.3464 16.6832C14.3464 16.7761 14.3464 16.8703 14.3464 16.9645C14.3464 17.0587 14.3464 17.1516 14.3464 17.2458C14.3464 17.34 14.3464 17.4342 14.3464 17.5271C14.3464 17.589 14.3464 17.6832 14.3464 17.8096C14.3464 17.8703 14.3464 17.9645 14.3464 18.0896C14.3464 18.1541 14.3464 18.2303 14.3464 18.3257C14.3464 18.4212 14.3464 18.5128 14.3464 18.6083C14.3464 18.669 14.3464 18.7619 14.3464 18.8883C14.3464 18.9502 14.3464 19.0444 14.3464 19.1709C14.3464 19.2328 14.3464 19.3102 14.3464 19.4057C14.3464 19.4973 14.3464 19.5928 14.3464 19.687C14.3464 19.7799 14.3464 19.8754 14.3464 19.9683C14.3464 20.0624 14.3464 20.1566 14.3464 20.2508C14.3464 20.3437 14.3464 20.4211 14.3464 20.4844C14.3464 20.6095 14.3464 20.7024 14.3464 20.7656C14.3464 20.8598 14.3464 20.954 14.3464 21.0469C14.3464 21.1398 14.3464 21.2353 14.3464 21.3282C14.3464 21.4237 14.3464 21.4998 14.3464 21.5643C14.3464 21.6882 14.3464 21.7824 14.3464 21.8443C14.3464 21.9385 14.3464 22.0327 14.3464 22.1269C14.3464 22.2211 14.3464 22.3153 14.3464 22.4082C14.3464 22.5011 14.3464 22.5965 14.3464 22.6907C14.3464 22.7823 14.3464 22.8778 14.3464 22.972C14.3464 23.0352 14.3464 23.1281 14.3464 23.2533C14.3464 23.3165 14.3464 23.4094 14.3464 23.5346C14.3464 23.5978 14.3464 23.6752 14.3464 23.7694C14.3464 23.8636 14.3464 23.9578 14.3464 24.0507C14.3464 24.1152 14.3464 24.2068 14.3464 24.3333C14.3464 24.3939 14.3464 24.4894 14.3464 24.6145C14.3464 24.6778 14.3464 24.7539 14.3464 24.8494C14.3464 24.9448 14.3464 25.0377 14.3464 25.1306C14.3464 25.2235 14.3464 25.319 14.3464 25.4119C14.3464 25.5061 14.3464 25.6003 14.3464 25.6945C14.3464 25.7887 14.3464 25.8661 14.3464 25.9293C14.3464 26.0532 14.3464 26.1474 14.3464 26.2106C14.3464 26.3035 14.3464 26.399 14.3464 26.4919C14.3464 26.5861 14.3464 26.679 14.3464 26.7732C14.3464 26.8673 14.3464 26.9461 14.3464 27.0067C14.3464 27.1331 14.3464 27.226 14.3464 27.2893C14.3464 27.3835 14.3464 27.4776 14.3464 27.5705C14.3464 27.6647 14.3464 27.7744 14.3464 27.8996V27.9938C14.3155 27.9938 14.269 27.9938 14.2071 27.9938V28.0389C14.1761 28.0389 14.1606 28.0557 14.1606 28.0867L14.1155 28.1796H14.0226V28.2273L13.9761 28.2725V28.3202H13.9297V28.4144C13.7142 28.4144 13.4974 28.4144 13.2807 28.4144V27.6634L13.3736 27.5693C13.4045 27.5693 13.4213 27.5551 13.4213 27.5228H13.4678V27.4673H13.5129V27.4209H13.5607C13.5607 27.3267 13.5607 27.2493 13.5607 27.186C13.5607 27.0931 13.5607 27.0299 13.5607 26.9977C13.5607 26.9048 13.5607 26.8106 13.5607 26.7177C13.5607 26.6235 13.5607 26.5293 13.5607 26.4364C13.5607 26.3422 13.5607 26.248 13.5607 26.1551C13.5607 26.0609 13.5607 25.9667 13.5607 25.8725C13.5607 25.8106 13.5607 25.7164 13.5607 25.5926C13.5607 25.528 13.5607 25.4506 13.5607 25.3577C13.5607 25.2648 13.5607 25.1693 13.5607 25.0739C13.5607 24.981 13.5607 24.8868 13.5607 24.7939C13.5607 24.7294 13.5607 24.6378 13.5607 24.5113C13.5607 24.4494 13.5607 24.372 13.5607 24.2778C13.5607 24.1836 13.5607 24.0894 13.5607 23.9965C13.5607 23.9023 13.5607 23.8081 13.5607 23.7152C13.5607 23.6197 13.5607 23.5268 13.5607 23.4326C13.5607 23.3385 13.5607 23.2597 13.5607 23.1978C13.5607 23.0714 13.5607 22.9772 13.5607 22.9152C13.5607 22.7901 13.5607 22.6972 13.5607 22.634C13.5607 22.5411 13.5607 22.4482 13.5607 22.3527C13.5607 22.2598 13.5607 22.1798 13.5607 22.1179C13.5607 21.9927 13.5607 21.8985 13.5607 21.8353C13.5607 21.7101 13.5607 21.6172 13.5607 21.5553C13.5607 21.4624 13.5607 21.3669 13.5607 21.2727C13.5607 21.1811 13.5607 21.0843 13.5607 20.9914C13.5607 20.8985 13.5607 20.8031 13.5607 20.7102C13.5607 20.6173 13.5607 20.5218 13.5607 20.4289C13.5607 20.3657 13.5607 20.2728 13.5607 20.1476C13.5607 20.0844 13.5607 20.007 13.5607 19.9128C13.5607 19.8186 13.5607 19.7244 13.5607 19.6302C13.5607 19.536 13.5607 19.4431 13.5607 19.3489C13.5607 19.287 13.5607 19.1915 13.5607 19.0676C13.5607 19.0057 13.5607 18.9283 13.5607 18.8328C13.5607 18.7373 13.5607 18.6444 13.5607 18.5502C13.5607 18.4251 13.5607 18.3322 13.5607 18.269C13.5607 18.1761 13.5607 18.0806 13.5607 17.9877C13.5607 17.8948 13.5607 17.8161 13.5607 17.7541C13.5607 17.6277 13.5607 17.5335 13.5607 17.4703C13.5607 17.3464 13.5607 17.2522 13.5607 17.189C13.5607 17.0961 13.5607 17.0019 13.5607 16.9103C13.5607 16.8148 13.5607 16.7361 13.5607 16.6742C13.5607 16.5477 13.5607 16.4394 13.5607 16.3465C13.5607 16.251 13.5607 16.1581 13.5607 16.0639C13.5607 16.002 13.5607 15.9078 13.5607 15.7826C13.5607 15.7194 13.5607 15.642 13.5607 15.5491C13.5607 15.4536 13.5607 15.3594 13.5607 15.2652C13.5607 15.171 13.5607 15.0781 13.5607 14.9839C13.5607 14.9207 13.5607 14.8278 13.5607 14.7027C13.5607 14.6394 13.5607 14.5633 13.5607 14.4678C13.5607 14.3723 13.5607 14.2807 13.5607 14.1865C13.5607 14.0924 13.5607 13.9982 13.5607 13.9053C13.5607 13.8111 13.5607 13.7169 13.5607 13.624C13.5607 13.5311 13.5607 13.4511 13.5607 13.3879C13.5607 13.2614 13.5607 13.1698 13.5607 13.1053C13.5607 12.9814 13.5607 12.8872 13.5607 12.8253C13.5607 12.7324 13.5607 12.6369 13.5607 12.5427C13.5607 12.4498 13.5607 12.3724 13.5607 12.3092C13.5607 12.184 13.5607 12.0886 13.5607 12.0279C13.5607 11.9015 13.5607 11.8099 13.5607 11.7454C13.5607 11.6525 13.5607 11.557 13.5607 11.4641C13.5607 11.3712 13.5607 11.2757 13.5607 11.1828C13.5607 11.0886 13.5607 10.9944 13.5607 10.9002C13.5607 10.8086 13.5607 10.7131 13.5607 10.6189C13.5607 10.557 13.5607 10.4628 13.5607 10.3364C13.5607 10.2744 13.5607 10.197 13.5607 10.1015V10.0564C13.5297 10.0564 13.4987 10.0564 13.4678 10.0564L13.4213 10.0099V9.98413L13.3736 9.93768V9.88994H13.3271V9.8422L13.2807 9.79575V9.74801L13.2342 9.70285V9.60866L13.1878 9.56221V9.51447H13.14V9.42157H13.0936V9.37512L13.0471 9.32738H13.0007C13.0007 9.29641 12.9852 9.28222 12.9529 9.28222V9.18803C12.9529 9.15706 12.9388 9.14287 12.9078 9.14287V9.09384H12.8588V9.00094C12.8291 9.00094 12.8123 8.98546 12.8123 8.9532C12.8123 8.92352 12.7968 8.90804 12.7671 8.90804V8.86159H12.7194V8.80998L12.673 8.71579C12.673 8.6874 12.6575 8.66934 12.6265 8.66934V8.62289L12.4872 8.53128V8.48354H12.442V8.39064C12.411 8.39064 12.3955 8.37258 12.3955 8.3429L12.3491 8.29774V8.25H12.3026V8.20355C12.2717 8.1713 12.2407 8.1571 12.211 8.1571V8.10936H12.1633V8.06291C12.1323 8.06291 12.1168 8.04614 12.1168 8.01646V7.92356C12.0872 7.92356 12.0704 7.90679 12.0704 7.87582L12.0239 7.82937V7.78292H11.9788L11.8394 7.59583V7.5468C11.8098 7.5468 11.7943 7.53132 11.7943 7.50035H11.7465V30.3756L11.562 30.4233C11.5001 30.4543 11.4369 30.5008 11.3749 30.5627H11.3298C10.5879 30.9691 9.89371 31.2517 9.24341 31.4065C8.47054 31.6568 7.72992 31.813 7.01769 31.8749C6.36868 32 5.73516 32.031 5.11712 31.9691C4.53134 31.9059 4.0062 31.8284 3.54299 31.7343C2.52367 31.5149 1.73532 31.2504 1.17921 30.9369C0.900509 30.8117 0.685034 30.7033 0.530201 30.6091C0.313435 30.5149 0.206343 30.453 0.206343 30.4208L0.0669934 30.373V7.45261C-0.0555825 7.20101 -0.010423 6.9365 0.206343 6.65522C0.206343 6.62426 0.221826 6.592 0.251502 6.56233C0.344402 6.53136 0.437302 6.51588 0.530201 6.51588V6.46685C0.685034 6.4062 0.886316 6.35846 1.13276 6.32879C1.3792 6.29782 1.73532 6.26556 2.19852 6.23331C2.44497 6.17137 2.70818 6.12492 2.98559 6.09396C3.20107 6.06428 3.3559 6.0488 3.4488 6.0488C3.41912 5.76623 3.41912 5.54817 3.4488 5.39205C3.51073 5.29915 3.55718 5.21915 3.58944 5.15722L3.7275 4.96884V2.9044H3.68105C3.52621 2.9044 3.35719 2.88892 3.17139 2.85666C2.95462 2.85666 2.73915 2.85666 2.52367 2.85666V2.2012H2.56883V2.15346C2.59851 2.15346 2.63076 2.15346 2.66044 2.15346C2.59851 2.06057 2.59851 1.91864 2.66044 1.73155C2.66044 1.63865 2.72237 1.48252 2.84624 1.26447C2.93914 1.04641 3.09397 0.919964 3.30945 0.888998H8.77891L9.01116 0.981898V1.17028C9.19696 0.797388 9.42792 0.467079 9.70662 0.185799V0.139349C9.70662 0.109673 9.70662 0.0774162 9.70662 0.0451593C9.7363 0.0451593 9.78404 0.0451593 9.84468 0.0451593C9.84468 0.0154831 9.86016 0 9.89242 0V0.0451593C10.0473 0.0451593 10.2008 0.0451593 10.3556 0.0451593V0.561268L10.4021 0.607718V0.654168C10.4627 0.654168 10.5414 0.654168 10.6343 0.654168V0.700618L10.6795 0.747068V0.888998ZM4.61004 10.357C3.86685 11.0151 3.27977 11.8292 2.84624 12.7956C2.22949 14.1401 1.91982 15.5 1.91982 16.8742C1.91982 17.9064 2.04369 18.8586 2.29142 19.7334C2.5998 20.8289 3.20365 21.6082 4.0991 22.0779C4.53134 22.3269 5.01132 22.4688 5.53517 22.4985C4.63972 21.9359 4.15974 21.0147 4.0991 19.7321C4.03716 18.7631 4.37779 17.8729 5.11841 17.0626C5.3055 16.9039 5.42937 16.811 5.4913 16.78C5.15196 17.749 5.52098 18.7464 6.60351 19.7799C7.12866 20.3102 7.51574 20.9205 7.76089 21.6069C7.88346 21.9514 7.85379 22.1849 7.66799 22.3114C8.07055 22.4675 8.48731 22.2933 8.91955 21.7966C8.93762 21.7746 8.9531 21.7527 8.966 21.7243C9.59695 21.3295 9.99822 20.6198 10.1711 19.5928C10.3569 18.3748 10.1569 17.1877 9.56856 16.0303C9.16729 15.1865 8.68859 14.4833 8.13249 13.9207C7.45122 13.1401 6.98931 12.2163 6.74157 11.1544C6.43191 9.99832 6.60351 9.01513 7.25123 8.20226C6.38417 8.85772 5.50549 9.57769 4.61004 10.357ZM11.2833 24.1397C11.2833 24.11 11.2833 24.0597 11.2833 24.0004V23.8584C8.7802 24.2352 7.03576 24.421 6.04612 24.421C5.02551 24.421 3.20365 24.2352 0.577941 23.8584C0.577941 23.8907 0.577941 23.9217 0.577941 23.9526C0.577941 23.9836 0.577941 24.0146 0.577941 24.0455V24.7036C0.577941 24.7319 0.577941 24.7655 0.577941 24.7965C0.577941 24.8261 0.577941 24.8584 0.577941 24.8894V24.9823C0.577941 25.0132 0.577941 25.0455 0.577941 25.0752C0.577941 25.1061 0.577941 25.1397 0.577941 25.1693C0.577941 25.2003 0.577941 25.2313 0.577941 25.2648V25.3577C0.577941 25.3874 0.577941 25.4184 0.577941 25.4493C0.577941 25.4803 0.577941 25.5126 0.577941 25.5422V25.5913C3.2359 26.0583 5.09002 26.2932 6.13902 26.2932C7.25123 26.2932 8.966 26.0738 11.2833 25.6377V25.1216C11.2833 25.0906 11.2833 25.0597 11.2833 25.0287V24.981C11.2833 24.919 11.2833 24.8713 11.2833 24.8416C11.2833 24.8106 11.2833 24.7797 11.2833 24.7474V24.7023C11.2833 24.6713 11.2833 24.639 11.2833 24.6081C11.2833 24.5771 11.2833 24.5281 11.2833 24.4687V24.4197C11.2833 24.3887 11.2833 24.3578 11.2833 24.3281C11.2833 24.2958 11.2833 24.2649 11.2833 24.2352C11.2833 24.2016 11.2833 24.1707 11.2833 24.1397ZM8.17765 22.4985H7.9454V22.5462L8.17765 22.4985ZM7.01769 22.5927V22.5462C6.98802 22.5462 6.97124 22.5617 6.97124 22.5927H7.01769ZM4.97906 22.7333C4.88874 22.723 4.80358 22.7075 4.72359 22.6869L4.70036 22.7333C4.79455 22.7333 4.88745 22.7333 4.97906 22.7333Z";
  }
}

//gives projectile color (user and projectile use different calls)
function getProjectileColor(projectile) {
  let grenade = getGrenadeFromGrenades(projectile.uniqueID);

  if (grenade.throwerSide === "CT") {
    return "#687cca";
  } else {
    return "#e38617";
  }
}

function handleKeyPress(e) {
  switch (e.key) {
    case "ArrowLeft":
      if (currentFrame.value - 16 > 1) {
        group.value.getNode().removeChildren();
        sliderFrame.value -= 16;
        currentFrame.value -= 16;
        loadTick();
      }
      break;
    case "ArrowRight":
      if(roundData.value["frames"][currentFrame.value + 16]) {
        group.value.getNode().removeChildren();
        sliderFrame.value += 16;
        currentFrame.value += 16;
        loadTick();
      }
      break;
    case " ":
      togglePlay();
      break;
    case "n":
      if (currentRound.value < totalRounds.value) {
        selectRound(currentRound.value + 1);
      }
      break;
    case "p":
      if (currentRound.value > 1) {
        selectRound(currentRound.value - 1);
      }
      break;
    case "f":
      increaseFrameRate();
      break;
    case "s":
      decreaseFrameRate();
      break;
    case "d":
      setIsDrawing(!isDrawing.value);
      break;
    case "c":
      clearLines();
      break;
    case "m":
      toggleMaps();
      break;
    default:
      // Handle other key presses if needed
      break;
  }
}

function setIsDrawing(value) {
  isDrawing.value = value;
  stageConfig.value.isDrawing = value;
}

function startDrawing(e) {
  if (!isDrawing.value) return;
  const { x, y } = getRelativePointerPosition();
  currentLine.value = { points: [x, y], stroke: 'red', strokeWidth: 2 };
  lines.value.push(currentLine.value);
}

function draw(e) {
  if (!currentLine.value) return;
  const { x, y } = getRelativePointerPosition();
  const newPoints = currentLine.value.points.concat([x, y]);
  currentLine.value.points = newPoints;
  lines.value[lines.value.length - 1] = currentLine.value;
  layer.value.getNode().draw();
}

function endDrawing() {
  if (!currentLine.value) return;
  currentLine.value = null;
}

function getRelativePointerPosition() {
  // the function will return pointer position relative to the passed node
  var transform = stage.value.getNode().getAbsoluteTransform().copy();
  // to detect relative position we need to invert transform
  transform.invert();

  // get pointer (say mouse or touch) position
  var pos = stage.value.getNode().getPointerPosition();

  // now we find a relative point
  return transform.point(pos);
}

function clearLines() {
  lines.value = [];
  layer.value.getNode().draw();
}

watch(currentTick, (newVal, oldVal) => {
  if (currentTick.value > roundData.value.endTick) {
    roundEnded.value = true;
  } else {
    roundEnded.value = false;
  }
  updateWeaponFires(newVal, oldVal);
  updateDamages(newVal, oldVal);
  removeNadeProjectiles(newVal, oldVal);
  updateKills(newVal, oldVal);
});

watch(sliderFrame, (newVal, oldVal) => {
  if (sliderFrame.value !== currentFrame.value) {
    currentFrame.value = Math.round(sliderFrame.value)
    group.value.getNode().removeChildren();
    currentFrame.value++;
    sliderFrame.value++;
    loadTick();
  }
});

watch(roundData, (newVal, oldVal) => {
  updatePlayers();
});

onBeforeMount(() => {
  subscribeToDemo();
  loadDemoData();
});

onMounted(() => {
  window.addEventListener('keydown', handleKeyPress);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyPress);
  if (subscription.value) {
    subscription.value.unsubscribe();
    subscription.value = null;
  }
  if (websocket.value) {
    websocket.value.disconnect();
    websocket.value = null;
  }
});

// Add this function to reset player states when changing rounds or clearing canvas
function clearPlayerStates() {
  playerStates.value = {};
}
</script>

<style lang="scss" scoped>
.custom-flex {
  flex: 1 1 1px;
}
.aspect-video {
  aspect-ratio: 16 / 9;
}
.bg-grid {
  background-image: radial-gradient(circle, #06090c 1px, rgba(0, 0, 0, 0) 1px);
  background-size: 30px 30px;
}
.width-transition {
  display: block;
  transition: all, 0.5s ease-in-out;
  width: 0;
}

.width-transition:focus {
  width: 16rem;
}
.video-move, /* apply transition to moving elements */
.video-enter-active,
.video-leave-active {
  transition: transform 0.5s ease;
}

.video-enter-from,
.video-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.video-leave-active {
}

.filter-red {
  filter: invert(34%) sepia(89%) saturate(3074%) hue-rotate(340deg) brightness(100%) contrast(88%);
}

.slider {
  -webkit-appearance: none; /* Override default CSS styles */
  appearance: none;
  width: 100%; /* Full-width */
  height: 100%; /* Specified height */
  background: none; /* Grey background */
  outline: none; /* Remove outline */
  opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
  -webkit-transition: 0.2s; /* 0.2 seconds transition on hover */
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none; /* Override default look */
  appearance: none;
  width: 5px; /* Set a specific slider handle width */
  height: 100%; /* Slider handle height */
  background: none; /* Green background */
  cursor: pointer; /* Cursor on hover */
}

.slider::-moz-range-thumb {
  width: 5px; /* Set a specific slider handle width */
  height: 100%; /* Slider handle height */
  background: none; /* Green background */
  cursor: pointer; /* Cursor on hover */
  border: none;
}

.slider::-webkit-slider-runnable-track {
  height: 100%;
}

.slider::-moz-range-track {
  height: 100%;
}

.cursor-pencil {
  cursor: url('../assets/icons/pencil.svg'), auto;

}
</style>
